/**
 * ============================================================
 *  GOOP ZONE — enemySystem.ts
 *  Runtime Enemy Management
 * ============================================================
 *
 *  Handles all live enemy instances during a run:
 *    - Creating enemy instances from templates
 *    - Movement AI (chase player)
 *    - Collision detection (enemy ↔ player, enemy ↔ projectile)
 *    - Damage dealing (touch damage with cooldowns)
 *    - Death, loot drops, and cleanup
 *    - Sprite loading and rendering
 *
 *  Called every frame by engine.ts update() and render().
 *
 *  Drop into: client/src/game/enemySystem.ts
 * ============================================================
 */

import {
  type EnemyDefinition,
  type EnemyInstance,
  type EnemyVariant,
  type SpriteDirection,
  type EnemyLootDrop,
  getEnemyDef,
  getScaledStats,
  getEnemySprite,
  rollEnemyDrops,
  PATHING_RADIUS_CAP,
  ENEMY_DATABASE,           // BETA 20.02 — for preloadAllEnemySprites()
} from './enemies';
import type { ThrowAttackDef, SlamAttackDef, EatAttackDef } from './enemies';
import { playBossSlam, playEnemyWallJump } from './gameSfx';
import { tuneOf } from './bunchaBullshiiii';   // BETA 21.75 — editable item effect magnitudes (Big Mac / Stay Frosty)
import { rollRng } from './rng'; // BETA 20.94 — seeded balance rolls (Big Mac proc)
// BETA 13.101 — boss slam crack as a persistent ground effect (60s lifetime,
// rendered under enemies/player via the weapon-system ground-effects pass).
import { spawnGroundEffectAt } from './weaponSystem';
import { getWeaponSystemState } from './weaponAdapter';
// BETA 16.59 — Wave 4: glow toggle for boss throw shadow-blur
import { getGlowEffectsEnabled as _glowEffectsEnabled } from './graphicsSettings';
// BETA 16.69 — Ugly Kittens lure: per-enemy override of steer target.
import { getKittenLureTarget } from './kittenLure';
import { getTrapLureTarget } from './trapLure';   // BETA 20.10 — banana/whoopie monkey-bomb lure
// BETA 14.04 — read player inventory + perc state for on-hit hooks (Big Mac
// burn DoT, Stay Frosty slow apply, Stompatine vs-named damage multiplier).
import { getItemState, getPercState } from './itemsAdapter';
// BETA 14.36 — retreat/warp event log per user: "build in a tracker in
// weapons testing for the csv to track those that run away and where they
// spawn in and how close and how long it takes to get to me."
import { pushRetreatStart, recordWarpEnd, recordReContact, getRetreatTuning, setLastDamageSource, trackSynergyTrigger, trackItemProc, trackPercCondition, trackFloorEffectTick } from './weaponLab';
// BETA 16.22 — death floor sprite decal on enemy death.
import { spawnDeathDecal } from './deathDecals';
// BETA 16.22 — clear gas overlay state on death (so cloud doesn't linger
// over the corpse decal). Independent of decal spawn.
import { clearGasStateForEnemy } from './gasOverlay';

// BETA 20.63 — TEMP chest-drop audit instrumentation. Counts where blue elite
// chests actually come from + the per-type kill volumes so a muted bot run can
// MEASURE the flood (Dylan: "how many elite chests spawned each run?"). Exposed
// on window for the test harness; reset at run start. Remove/convert to real
// telemetry once the every-10 fix lands.
const __chestAudit = {
  // E1 (enemy rebuild) — fromTimmy/fromHeavy/killsTimmy/killsHeavy removed:
  // permanently-zero window-only debug keys for deleted enemies (never CSV columns).
  total: 0, fromEliteGate: 0,
  killsEliteVariant: 0, killsTotal: 0,
};
if (typeof window !== 'undefined') {
  (window as unknown as { __chestAudit: typeof __chestAudit }).__chestAudit = __chestAudit;
  (window as unknown as { __resetChestAudit: () => void }).__resetChestAudit = () => {
    (Object.keys(__chestAudit) as (keyof typeof __chestAudit)[]).forEach(k => { __chestAudit[k] = 0; });
  };
}

// BETA 14.12 — Cached player stats for item-on-hit scaling (Manus handoff
// Section A: hardcoded item damage values needed to scale with player perks).
// Engine.ts calls setCurrentPlayerStats() each frame with the live snapshot;
// damageEnemy reads from this cache to scale Big Mac burn DoT damage / proc
// chance / duration, Stay Frosty slow duration, etc. Keeps damageEnemy's
// signature unchanged (would otherwise need to plumb player through every caller).
let _cachedPlayerStats: {
  damageBonus: number;
  attackSpeedBonus: number;
  attackAreaBonus: number;
  luck: number;
} = { damageBonus: 0, attackSpeedBonus: 0, attackAreaBonus: 0, luck: 0 };
export function setCurrentPlayerStats(s: Partial<typeof _cachedPlayerStats>): void {
  _cachedPlayerStats = {
    damageBonus: s.damageBonus ?? 0,
    attackSpeedBonus: s.attackSpeedBonus ?? 0,
    attackAreaBonus: s.attackAreaBonus ?? 0,
    luck: s.luck ?? 0,
  };
}
import type { PlacedRoom } from './roomMapgen';
import { ROOM_SIZE, GAP_DEPTH, GAP_WIDTH, GAP_OFFSET, WORLD_SIZE } from './roomMapgen';
import { BOUNDLESS_MAP } from './mapChunks';   // 23.55 (audit) — legacy 0..WORLD_SIZE clamps must not fire in the boundless world
import { BORDER_COLLISION_NORTH, BORDER_COLLISION_SOUTH, BORDER_COLLISION_WEST, BORDER_COLLISION_EAST } from './borderWalls';
import type { GameState } from './types';
import { getGreenZoneRedirect, isWalkable } from './types';
import { getDoorwayMagnet } from './roomCollisionGrid';
import { findPath, getNavGraph } from './navGraph';
import { getFlowDirection, isFlowFieldReady, isFlowCellWalkable } from './flowField';
import { debugEnemyHpMult } from './debugState';

// ─── Constants ────────────────────────────────────────────────
// BETA 14.35 — User: "should be spawning slightly outside my FOV". Was 200-450wu
// outside viewport edge (so enemies were 800-1050wu from player center; took
// 8-10s to walk back into view at zombie speed 100wu/s). User saw 50 enemies
// leave and only a few return. Now 50-150wu past the edge → enemies are at
// 650-750wu from player center → reach the screen edge in 0.5-1.5s.
const SPAWN_MARGIN = 50;              // Min distance outside BASE-ZOOM viewport to spawn
const SPAWN_MAX_DISTANCE = 150;       // Max distance outside BASE-ZOOM viewport to spawn
const BASE_SPAWN_ZOOM = 1.0;          // Always use this zoom for spawn-ring math — dev zoom-out must NOT push spawn ring out of despawn range
const DESPAWN_DISTANCE = 2500;        // Remove enemies this far from player (must be > furthest possible spawn)
const HIT_FLASH_DURATION = 0.18;      // BETA 20.69 — 0.12 → 0.18 (VFX audit: too fast for the brain to register the hit; 0.18 + brighter tint = visible impact)
const KNOCKBACK_FRICTION = 0.85;      // Knockback velocity decay per frame
const KNOCKBACK_MIN = 0.5;            // Stop knockback below this velocity
// BETA 22.03 — cap accumulated enemy knockback. Total fling ≈ value·(1/(1−friction)) = value·6.7
// world-units (move loop = knockback·dt·60). 200 → ~1330wu max fling, safely under the 2500
// despawn cull, so a stacked multi-hit can't rocket a regular enemy off-map + delete it (no loot).
const ENEMY_KNOCKBACK_CAP = 200;
const SEPARATION_RADIUS = 80;         // BETA 14.73 cycle-9: 46 → 80. Post-BUG-2 hitboxes (player 32, zombie 24, touchDist 56), 46 let enemies overlap into player. 80 = enemies form a "ring" outside touch radius (~2-3 in attack zone instead of 4-6) — classic VS-style horde feel, halves incoming damage rate.
const SEPARATION_FORCE = 90;          // Push-apart strength
// BETA 20.78 — slam-telegraph colors, hoisted out of the render loop
// (perf law: no per-frame string building). Earth/shadow palette — this is a
// ground shadow + cracking dirt, not an energy effect.
const SLAM_SHADOW_FILL = '#140a04';   // near-black warm shadow blob
const SLAM_CORE_FILL = '#000000';     // dead-center impact core
const SLAM_CRACK_STROKE = '#2e1808';  // dark dirt-crack brown
// BETA 20.78 — frozen-status ice-spike colors (two different blues so the
// pair reads as jagged ice, not mirrored clip-art).
const FROZEN_SPIKE_LIGHT = '#cfeeff';
const FROZEN_SPIKE_DEEP = '#6fc8ef';

// ─── Room-Aware Navigation ────────────────────────────────────
// Enemies route through doorway gap centers when in a different room than the player.
// Waypoints are recalculated every ~0.5s per enemy (staggered to spread CPU cost).
// This prevents enemies from wedging against walls trying to walk through solid stone.

/** Find which room strictly contains (x, y). No outward margin — exited enemies go to direct chase immediately. */
function _roomContaining(rooms: PlacedRoom[], x: number, y: number): PlacedRoom | null {
  for (const room of rooms) {
    if (x >= room.x && x < room.x + room.size &&
        y >= room.y && y < room.y + room.size) {
      return room;
    }
  }
  return null;
}

/**
 * Set enemy.navWaypointX/Y to the center of the gap the enemy should walk through
 * to get from its room toward the player's room. If both are in the same room (or
 * neither is in a room), clear the waypoint so the enemy steers directly to the player.
 */
function _updateNavWaypoint(
  enemy: EnemyInstance,
  rooms: PlacedRoom[],
  playerX: number,
  playerY: number,
  gameState?: GameState,
): void {
  const enemyRoom = _roomContaining(rooms, enemy.x, enemy.y);

  // Not in a room = open ground, no walls to navigate around → go direct
  if (!enemyRoom) {
    enemy.navWaypointX = 0;
    enemy.navWaypointY = 0;
    enemy.navPath = [];
    return;
  }

  const playerRoom = _roomContaining(rooms, playerX, playerY);

  enemy.navHoldRoom = false;

  // Same room → go direct
  if (playerRoom && playerRoom.id === enemyRoom.id) {
    enemy.navWaypointX = 0;
    enemy.navWaypointY = 0;
    enemy.navPath = [];
    return;
  }

  // BETA 15.3 — REAL A* PATHFINDING through the navGraph for multi-room
  // navigation. User: "smart pathfinding in rooms for bots and enemies
  // would be great. some rooms are 4 rooms touching and they just get
  // trapped." The previous cardinal-direction heuristic only worked for
  // adjacent rooms; in multi-room clusters with 4 connecting doorways
  // it picked the wrong doorway and enemies got stuck.
  //
  // Now: when enemyRoom != playerRoom, call findPath() which traverses
  // the actual room→doorway connectivity graph. Store the full path in
  // enemy.navPath; set navWaypointX/Y to the first waypoint. The
  // existing waypoint-advance logic (line ~866) pops waypoints as the
  // enemy reaches each one, so the full chain plays out automatically.
  if (gameState?.map?.roomMap) {
    const path = findPath(gameState.map.roomMap, enemy.x, enemy.y, playerX, playerY);
    if (path && path.length > 0) {
      enemy.navPath = path;
      enemy.navWaypointX = path[0].x;
      enemy.navWaypointY = path[0].y;
      return;
    }
  }

  // Fallback: legacy cardinal-direction heuristic (when navGraph isn't
  // available — e.g., in tests, lab mode, or before map is built).
  const egx = Math.round(enemyRoom.x / ROOM_SIZE);
  const egy = Math.round(enemyRoom.y / ROOM_SIZE);
  const pgx = playerRoom ? Math.round(playerRoom.x / ROOM_SIZE) : Math.round(playerX / ROOM_SIZE);
  const pgy = playerRoom ? Math.round(playerRoom.y / ROOM_SIZE) : Math.round(playerY / ROOM_SIZE);
  const ddx = pgx - egx;
  const ddy = pgy - egy;

  let targetDir: string;
  if (Math.abs(ddx) >= Math.abs(ddy)) {
    targetDir = ddx > 0 ? 'east' : 'west';
  } else {
    targetDir = ddy > 0 ? 'south' : 'north';
  }

  const exitPush = GAP_DEPTH * 2;
  const gap = enemyRoom.gaps.find(g => g.dir === targetDir);
  if (!gap) {
    const fallback = enemyRoom.gaps[0];
    if (fallback) {
      switch (fallback.dir) {
        case 'north': enemy.navWaypointX = fallback.x + fallback.w / 2; enemy.navWaypointY = fallback.y - exitPush; break;
        case 'south': enemy.navWaypointX = fallback.x + fallback.w / 2; enemy.navWaypointY = fallback.y + fallback.h + exitPush; break;
        case 'east':  enemy.navWaypointX = fallback.x + fallback.w + exitPush; enemy.navWaypointY = fallback.y + fallback.h / 2; break;
        case 'west':  enemy.navWaypointX = fallback.x - exitPush; enemy.navWaypointY = fallback.y + fallback.h / 2; break;
      }
    } else {
      enemy.navWaypointX = 0;
      enemy.navWaypointY = 0;
    }
    enemy.navPath = [];
    return;
  }

  switch (gap.dir) {
    case 'north': enemy.navWaypointX = gap.x + gap.w / 2; enemy.navWaypointY = gap.y - exitPush; break;
    case 'south': enemy.navWaypointX = gap.x + gap.w / 2; enemy.navWaypointY = gap.y + gap.h + exitPush; break;
    case 'east':  enemy.navWaypointX = gap.x + gap.w + exitPush; enemy.navWaypointY = gap.y + gap.h / 2; break;
    case 'west':  enemy.navWaypointX = gap.x - exitPush; enemy.navWaypointY = gap.y + gap.h / 2; break;
  }
  enemy.navPath = [];
}

// Keep GAP_WIDTH/GAP_OFFSET in scope for the waypoint-reach threshold
const _NAV_REACH_RADIUS_SQ = (GAP_WIDTH * 0.45) ** 2; // ~86px — enemy considered "through gap" when within this distance

// ─── Enemy Projectile Pool ────────────────────────────────────
interface EnemyProjectile {
  id: number;
  x: number; y: number;
  vx: number; vy: number;
  damage: number;
  radius: number;
  spriteUrl: string;
  rotation: number;
  rotationSpeed: number;
  ownerId: number;
  /** BETA 20.86 — the THROWER's definitionId ('timmy',…) so a fatal
   *  throw is attributed to who threw it, not the nearest enemy at impact. */
  ownerType?: string;
  isBoss: boolean;
  scale: number;
  travelDist: number;
  maxDist: number;
  // BETA 17.11 — PSBOT cycle-30ab: Dylan complaint "all things that can be
  // thrown need a red outline on it. so zombies that are thrown by caseo
  // need red outlined on it just like timmy's penguin throw." Previously
  // the red-outline render gate at line ~2426 only fired on `isBoss`
  // (variant === 'boss'). Regular elites' thrown zombies had no
  // outline. New flag lets renderer outline any high-danger projectile
  // regardless of source enemy's boss variant.
  shouldOutline?: boolean;
}

const _projectiles: EnemyProjectile[] = [];
let _nextProjectileId = 1;

export function clearEnemyProjectiles(): void {
  _projectiles.length = 0;
}

// BETA 17.21 — PSBOT cycle-30ai B2: expose projectiles to autoPlaytester
// so bot can dodge incoming throws. Returns a read-only snapshot of
// active projectiles with position + velocity + radius.
export function getActiveEnemyProjectiles(): ReadonlyArray<{ x: number; y: number; vx: number; vy: number; radius: number }> {
  return _projectiles;
}

// BETA 16.33 — slam-impact screen-shake callback. Dylan: "game
// should shake on every boss jump attack when it hits the ground
// pretty wild like." Engine registers a handler that pokes
// state.screenShake* directly. enemySystem can't import GameState
// without a cycle, so we route through this lambda.
// BETA 16.52 — extended signature: second arg true for mega-tier bosses
// (final boss) so the engine can ramp the shake higher than a regular
// boss. BETA 16.53 — added impactX/Y/aoeRadius so the engine can do
// room-destruction at the impact point (mega-only). All extras
// optional — old 1-arg call sites still compile.
type SlamImpactCb = (
  isBoss: boolean,
  isMega?: boolean,
  impactX?: number,
  impactY?: number,
  aoeRadius?: number,
) => void;
let _slamImpactShakeCb: SlamImpactCb | null = null;
export function registerSlamImpactShake(cb: SlamImpactCb | null): void {
  _slamImpactShakeCb = cb;
}

// BETA 20.72 — Death-dust callback. When a corpse decal lands, the engine
// spawns settling dust motes at the body so deaths land with weight.
// enemySystem has no particles array and can't import GameState without a
// cycle, so this routes through a registered lambda — same pattern as the
// slam-shake callback above.
type DeathDustCb = (x: number, y: number, spriteSize: number) => void;
let _deathDustCb: DeathDustCb | null = null;
export function registerDeathDustSpawner(cb: DeathDustCb | null): void {
  _deathDustCb = cb;
}

// BETA 20.78 — enemy PROJECTILE IMPACT splats (audit: thrown bottles/debris
// vanished silently on wall/player hit — zero impact feedback). Same bridge
// pattern as death dust above; engine registers a particle spawner at init.
// Fired on wall-kill and player-hit only (max-range expiry hits nothing).
type ProjImpactCb = (x: number, y: number, isBoss: boolean) => void;
let _projImpactCb: ProjImpactCb | null = null;
export function registerProjectileImpactSpawner(cb: ProjImpactCb | null): void {
  _projImpactCb = cb;
}

// BETA 16.31 — Enemy throwable speed ramp.
// Dylan: "im 4 minutes left in the game. at that moment all enemie's
// throwables should be moving twice the speed they are. and gradually get
// faster over time."
// BETA 19.98 — Tracks waveState.currentFloor so getScaledStats can apply the
// floor multiplier without enemySystem needing direct access to waveState.
// Set each tick by enemyAdapter.update before any spawns happen.
let _currentFloorForScaling = 0;
export function setCurrentFloorForScaling(floor: number): void {
  _currentFloorForScaling = floor;
}

// BETA 21.19 — GOD MODE escalation (Dylan: killing the final boss no longer ends
// the run — it keeps scaling forever until you die). These multipliers are applied
// to NEW enemy spawns' HP / damage / speed on top of all normal scaling. The engine
// recomputes them each frame on a LINEAR ramp (overflow-safe — the old exponential
// endless cap exists precisely because pow(2,t) hit Infinity and crashed). Default
// 1 = no effect outside god mode. Speed is separately capped by the engine.
let _godHpMult = 1, _godDmgMult = 1, _godSpdMult = 1;
export function setGodModeScaling(hp: number, dmg: number, spd: number): void {
  _godHpMult = Number.isFinite(hp) ? hp : 1;
  _godDmgMult = Number.isFinite(dmg) ? dmg : 1;
  _godSpdMult = Number.isFinite(spd) ? spd : 1;
}

// BETA 20.77 — clock fix: state.time is monotonic now, so the ENDLESS
// multiplier (kicks in at 10:00, "after the floor timer ends") must run on
// FLOOR-LOCAL seconds or floor 2 (starting ~10 min into the run) would
// instantly hit endless ×2+. The base stat RAMP keeps the monotonic clock
// (Dylan's spec: floor-2 enemies = "same strength as end of floor 1").
// Set each tick by enemyAdapter.update from waveState.floorStartTime.
let _floorStartForScaling = 0;
export function setFloorStartForScaling(t: number): void {
  _floorStartForScaling = t;
}

// BETA 21.31 — ENDLESS crowd-control resistance. Dylan: late-game enemies should
// resist knockback + slows/freeze MORE so the swarm can actually CLOSE IN (a strong
// knockback or slow build was walling off the whole swarm so he was never touched —
// only 231 hits in 20 min). Ramps 0 → 0.7 from 10:00 → 16:00 on the global clock;
// set per-frame by enemyAdapter. Applied to knockback force + slow/freeze duration.
let _endlessCcResist = 0;
export function setEndlessCcResist(v: number): void {
  _endlessCcResist = Number.isFinite(v) ? Math.max(0, Math.min(0.7, v)) : 0;
}

// Set each tick by enemyAdapter.update from waveState.floorTimeRemaining
// + finalSwarm state. Default 1.0 = no change. Read in spawnEnemyProjectile.
let _enemyProjSpeedMult = 1.0;
export function setEnemyProjectileSpeedMultiplier(m: number): void {
  _enemyProjSpeedMult = Math.max(1.0, m);
}
export function getEnemyProjectileSpeedMultiplier(): number {
  return _enemyProjSpeedMult;
}

// ─── Flank Warp State ─────────────────────────────────────────
// Tracks player velocity so off-screen lagging enemies can be warped ahead of the player.
// BETA 23.6x — CO-OP PHASE 2 part B: velocity is tracked PER PLAYER. The old
// single _playerVx/_playerVy pair became garbage the moment 2+ players fed it
// through one code path (the §2 landmine in the design doc) — predictive
// intercept and flank spawns would chase a phantom average. Keyed by
// view.playerId; SP uses exactly one entry, same math, same SMOOTH constant,
// same first-frame spike behavior (lastX starts 0) — byte-identical.
interface TrackedPlayerVel { vx: number; vy: number; lastX: number; lastY: number }
const _playerVels = new Map<string, TrackedPlayerVel>();
function velOf(id: string | undefined): TrackedPlayerVel {
  const k = id ?? '_solo';
  let v = _playerVels.get(k);
  if (!v) { v = { vx: 0, vy: 0, lastX: 0, lastY: 0 }; _playerVels.set(k, v); }
  return v;
}
/** CO-OP PHASE 2 — world-scoped, reset with the run (adapter init calls this). */
export function resetPlayerVelocityTracker(): void { _playerVels.clear(); }

// CO-OP PHASE 2 part B — nearest LIVING player view to a world point (per
// enemy, per frame; trivial for ≤4 players). Falls back to the primary view
// so an all-downed roster can never hand the sim undefined mid-frame.
function nearestOfPlayers(players: PlayerForEnemy[], x: number, y: number): PlayerForEnemy {
  if (players.length === 1) return players[0];
  let best: PlayerForEnemy | null = null;
  let bestD2 = Infinity;
  for (const pv of players) {
    if (pv.currentHP <= 0) continue;
    const dx = pv.x - x, dy = pv.y - y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) { bestD2 = d2; best = pv; }
  }
  return best ?? players[0];
}
// BETA 14.42 — "Recently moved" grace window. Independent of the smoothed
// _playerVx/Vy (which can avg to ~0 during strafing). Tracks per-frame
// raw velocity magnitude; whenever it exceeds threshold, this resets to
// 0.5s. Decays toward 0 each frame. Used by retreat-gate so strafing
// counts as "moving" even though smoothed velocity oscillates.
let _movementGraceSec = 0;
let _warpTimer = 5.0;

// BETA 14.82 — Fix 2 (PSBOT RUN cycle-11): per-enemy stuck-detection map.
// Sampled every 1s; if enemy has moved <50wu in 5s with the player as a target,
// despawn after 3 consecutive stuck samples. Cleared per enemy id when not stuck.
interface EnemyStuckSample { x: number; y: number; lastSampleT: number; stuckSamples: number; }
const _enemyStuckTracking = new Map<number, EnemyStuckSample>();

// BETA 20.04 — id → enemy lookup cache. Was 3 sites doing state.enemies.find()
// per call (damageEnemy + applyEnemyStatus + stripEnemyDefenses), each O(N).
// At 200 enemies × thousands of damage events per second = 35K array scans/sec
// (the audit's #6 hot spot).
//
// BETA 20.05 — CRITICAL HOTFIX. Initial 20.04 version only invalidated cache
// on length change. But cleanupDead at line 2189 REASSIGNS state.enemies (via
// .filter), and if 2 enemies die + 2 spawn in same frame, length is identical
// but contents differ — cache returned wrong/missing enemies. Dylan: "sniper
// not making any health go down on enemies." Now we ALSO check the array
// reference itself — `state.enemies !== _enemyByIdMapArr` catches every
// cleanupDead reassignment.
let _enemyByIdMap: Map<number, EnemyInstance> | null = null;
let _enemyByIdMapArr: EnemyInstance[] | null = null;
let _enemyByIdMapLen = -1;
function _findEnemyById(state: EnemySystemState, id: number): EnemyInstance | undefined {
  if (
    _enemyByIdMap === null ||
    _enemyByIdMapArr !== state.enemies ||
    _enemyByIdMapLen !== state.enemies.length
  ) {
    _enemyByIdMap = new Map();
    for (let i = 0; i < state.enemies.length; i++) {
      _enemyByIdMap.set(state.enemies[i].id, state.enemies[i]);
    }
    _enemyByIdMapArr = state.enemies;
    _enemyByIdMapLen = state.enemies.length;
  }
  return _enemyByIdMap.get(id);
}

// BETA 20.02 — Preload ALL enemy sprites at game start. Was lazy-loaded per
// enemy variant on first spawn (loadSprite called inside spawnEnemy at line
// 697-708). Result: first zombie spawn triggers a network fetch + parse +
// GPU upload mid-frame = visible stutter. Dylan: "lag in the beginning"
// — almost certainly first-spawn sprite loads firing during gameplay.
// initGame() now calls this once before the player can move so every enemy
// sprite is already in the spriteCache + GPU-decoded.
export function preloadAllEnemySprites(): void {
  for (const def of Object.values(ENEMY_DATABASE)) {
    if (def.sprites?.up) loadSprite(def.sprites.up);
    if (def.sprites?.left) loadSprite(def.sprites.left);
    if (def.sprites?.right) loadSprite(def.sprites.right);
    if (def.eliteSprite) loadSprite(def.eliteSprite);
    if (def.throwAttack?.throwSprite) loadSprite(def.throwAttack.throwSprite);
    if (def.throwAttack?.projectileSprite) loadSprite(def.throwAttack.projectileSprite);
    if (def.slamAttack?.airborneSprite) loadSprite(def.slamAttack.airborneSprite);
    if (def.slamAttack?.crackSprite) loadSprite(def.slamAttack.crackSprite);
  }
}

// ─── Sprite Cache ─────────────────────────────────────────────
const spriteCache: Map<string, HTMLImageElement> = new Map();

function loadSprite(url: string): HTMLImageElement {
  if (spriteCache.has(url)) {
    return spriteCache.get(url)!;
  }
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    // BETA 17.28 — pre-decode so first drawImage of a new enemy sprite
    // doesn't stall the frame (was contributing to 1604ms decode total
    // in Dylan's DevTools profile).
    img.decode?.().catch(() => { /* fallback to lazy decode */ });
    console.log('[Enemy] Sprite loaded:', url.slice(-20));
  };
  img.onerror = (e) => { console.error('[Enemy] Sprite FAILED:', url.slice(-20), e); };
  img.src = url;
  spriteCache.set(url, img);
  return img;
}

/**
 * Load a sprite and strip near-white pixels so assets without an alpha channel
 * (saved as RGB PNG) display with a transparent background.
 * Threshold R>240 AND G>240 AND B>240 → alpha=0.
 */
function loadSpriteTransparent(url: string): HTMLImageElement {
  if (spriteCache.has(url)) return spriteCache.get(url)!;
  const result = new Image();
  spriteCache.set(url, result);
  const raw = new Image();
  raw.crossOrigin = 'anonymous';
  raw.onerror = (e) => { console.error('[Enemy] Transparent sprite FAILED:', url, e); };
  raw.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width  = raw.naturalWidth;
    canvas.height = raw.naturalHeight;
    const ctx2 = canvas.getContext('2d');
    if (!ctx2) { result.src = url; return; }
    ctx2.drawImage(raw, 0, 0);
    const imageData = ctx2.getImageData(0, 0, canvas.width, canvas.height);
    const d = imageData.data;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i] > 240 && d[i + 1] > 240 && d[i + 2] > 240) d[i + 3] = 0;
    }
    ctx2.putImageData(imageData, 0, 0);
    result.src = canvas.toDataURL('image/png');
    console.log('[Enemy] White-removed sprite ready:', url.slice(-30));
  };
  raw.src = url;
  return result;
}

// ─── Enemy System State ──────────────────────────────────────
export interface EnemySystemState {
  enemies: EnemyInstance[];
  nextId: number;
  totalKills: number;
  totalEliteKills: number;
  firstEliteKilledThisFloor: boolean;
  aliveCount: number;   // O(1) alive enemy count — incremented/decremented in place
  /** BETA 20.49 — Blue Elite Chest: every elite death pushes its death pos here;
   *  the engine drains this each frame and spawns a free blue legendary chest. */
  pendingEliteChestSpawns?: Array<{ x: number; y: number }>;
  /** BETA 20.56 — counts elite kills; a blue chest drops every 10th. */
  eliteChestCounter?: number;
  /** BETA 20.58 — count of enemies removed WITHOUT dying (the "vanishing elite" tracker). */
  enemyDespawnsNoDeath?: number;
}

/** Create a fresh enemy system state for a new run */
export function createEnemySystemState(): EnemySystemState {
  // Per-item AoE damage telemetry resets with the run (BETA 13.23).
  _itemDamageStats.clear();
  // 23.55 (audit) — enemy ids restart at 1 each run; stale stuck-tracking
  // entries from the previous run disabled stuck-despawn for reused ids.
  _enemyStuckTracking.clear();
  return {
    enemies: [],
    nextId: 1,
    totalKills: 0,
    totalEliteKills: 0,
    firstEliteKilledThisFloor: false,
    aliveCount: 0,
    pendingEliteChestSpawns: [],
    eliteChestCounter: 0,
    enemyDespawnsNoDeath: 0,
  };
}

// ─── Item damage telemetry (BETA 13.23) ──────────────────────────────────────
// Items like Anus Rocket (R08), Diarrhea Gas (R11), and Nutty T-Bag (R16) deal
// damage via the AoE-request queue, bypassing WeaponInstance entirely. We
// accumulate per-item totals here so the bot CSV can attribute their output
// instead of showing 0 damage.
export interface ItemDamageStats {
  totalDamageDealt: number;
  hits: number;
}
const _itemDamageStats: Map<string, ItemDamageStats> = new Map();

export function getItemDamageStats(): ReadonlyMap<string, ItemDamageStats> {
  return _itemDamageStats;
}

export function resetItemDamageStats(): void {
  _itemDamageStats.clear();
}

function _trackItemDamage(itemId: string, dmg: number): void {
  let stat = _itemDamageStats.get(itemId);
  if (!stat) {
    stat = { totalDamageDealt: 0, hits: 0 };
    _itemDamageStats.set(itemId, stat);
  }
  stat.totalDamageDealt += dmg;
  stat.hits += 1;
}

// ─── Player Interface (what we need from engine.ts) ──────────
export interface PlayerForEnemy {
  // BETA 23.6x — CO-OP PHASE 2: which roster slot this view belongs to
  // ('p1'..'p4', matches types.ts PlayerId / net wire ids). Optional so
  // hand-built test views keep compiling; the adapter always sets it.
  // Bram's E2+ damage-attribution ledger consumes this id.
  playerId?: string;
  x: number;
  y: number;
  facingAngle?: number;    // radians; 0 = right — used to bias spawn toward player's front
  hitboxRadius: number;
  currentHP: number;
  maxHP: number;
  // Called when an enemy attack lands on this player. CO-OP part B (§5): the
  // attack SOURCE rides the call itself ('zombie' / '<id>_slam' / '<id>_throw')
  // — the setLastDamageSource module global stays as the SP-compat mirror, but
  // two enemies hitting two players in one frame can no longer cross-tag.
  damage: (amount: number, iFrameGrant: number, source?: string) => void;
  knockbackResist: number;            // 0-1, how much player resists knockback
  iFrameTimer: number;                // Current i-frame countdown
  // BETA 22.02 — contact knockback + Timmy-slow the enemy system WRITES on a hit. These MUST
  // proxy back to the real state.player (via getter/setter in toPlayerForEnemy) or the shove is
  // written to a throwaway adapter and lost — which is why no contactKnockback value ever worked.
  knockbackX?: number;
  knockbackY?: number;
  slowUntil?: number;
  thorns?: number;                    // % of touch damage reflected back to attacker
  // BETA 19.51 — Megabonk-style curve balance: enemy spawn cap is gated on
  // player level so an under-leveled player gets breathing room to find
  // weapons + level up. Optional so legacy callers (lab harnesses etc.)
  // don't break; defaults to 1 in waveSystem if missing.
  level?: number;
}

// ─── Camera Interface ────────────────────────────────────────
export interface CameraForEnemy {
  x: number;
  y: number;
  viewportWidth: number;
  viewportHeight: number;
  zoom: number;
}

// ─── Steering Helper ──────────────────────────────────────────

function steerTowardPlayer(
  enemy: EnemyInstance,
  targetX: number,
  targetY: number,
  speed: number,
  dt: number,
  isBlocked?: (x: number, y: number, r: number) => boolean,
): void {
  const step = speed * dt;
  // BETA 15.46 — movement uses pathingRadius (capped at 50) so big enemies
  // fit through doorways. hitboxRadius (full size) stays in damage paths.
  const r = enemy.pathingRadius;

  // BETA 14.69 — STEERING PRIORITY INVERTED.
  // User: "the red line is not pointing at the bot. and is flickering like
  // a jump. so the enemies are having trouble finding the bot acuratly
  // smoothly." Root cause: flow field was PRIMARY steering. Flow field
  // uses pre-computed grid cells — as enemies cross cell boundaries the
  // direction snaps to the new cell's pre-computed vector, causing visible
  // direction jumps. Plus the field is rebuilt periodically, not every
  // frame, so player movement makes the field stale until rebuild.
  //
  // New priority for VS-style smooth beeline:
  //   1. Direct steering (continuous, points exactly at player every frame)
  //   2. Flow field fallback (only when direct path is blocked by a wall)
  //   3. Probe angles (fallback when both blocked)
  //   4. Escape (fully stuck)
  //
  // Net effect: enemies in open space (most of the time) point straight at
  // the player with zero flicker. Flow field still handles the cases it's
  // good at: routing around walls, through doorways, around exterior green
  // curves.

  const dx = targetX - enemy.x;
  const dy = targetY - enemy.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist < 1) return;

  // ── LAYER 1: Direct steering (primary) ──
  const nx = (dx / dist) * step;
  const ny = (dy / dist) * step;
  if (!isBlocked || !isBlocked(enemy.x + nx, enemy.y + ny, r)) {
    enemy.x += nx;
    enemy.y += ny;
    return;
  }

  // ── LAYER 2: Flow field (fallback when direct is blocked) ──
  // Used for routing around walls / through doorways. Cell-aligned snapping
  // is acceptable here because the enemy was about to walk into a wall
  // anyway — visual jump is a side-effect of route changes, not stale data.
  if (isFlowFieldReady()) {
    const flowDir = getFlowDirection(enemy.x, enemy.y);
    if (flowDir) {
      const fnx = flowDir.x * step;
      const fny = flowDir.y * step;
      if (!isBlocked(enemy.x + fnx, enemy.y + fny, r)) {
        enemy.x += fnx;
        enemy.y += fny;
        return;
      }
    }
  }

  // ── LAYER 3: Probe angles (when both direct + flow blocked) ──
  const baseAngle = Math.atan2(dy, dx);
  const probeAngles = [
    baseAngle - 0.27, baseAngle + 0.27,   // ±15°
    baseAngle - 0.55, baseAngle + 0.55,   // ±31°
    baseAngle - 0.82, baseAngle + 0.82,   // ±47°
    baseAngle - 1.1,  baseAngle + 1.1,    // ±63°
    baseAngle - 1.57, baseAngle + 1.57,   // ±90° (pure sideways)
  ];
  for (const angle of probeAngles) {
    const pnx = Math.cos(angle) * step;
    const pny = Math.sin(angle) * step;
    if (!isBlocked(enemy.x + pnx, enemy.y + pny, r)) {
      enemy.x += pnx;
      enemy.y += pny;
      return;
    }
  }

  // ── LAYER 4: Fully stuck — cycle through 8 escape directions over time ──
  const timeBucket = Math.floor(Date.now() / 400);
  const escapeIdx  = (enemy.id * 7 + timeBucket) % 8;
  const escAngle   = (escapeIdx / 8) * Math.PI * 2;
  enemy.x += Math.cos(escAngle) * step * 0.6;
  enemy.y += Math.sin(escAngle) * step * 0.6;
}

// ═══════════════════════════════════════════════════════════════
//  SPAWN ENEMY
// ═══════════════════════════════════════════════════════════════

/**
 * Spawn a new enemy instance around the player, outside the viewport.
 * Returns the created instance (also added to state.enemies).
 */
export function spawnEnemy(
  state: EnemySystemState,
  definitionId: string,
  variant: EnemyVariant,
  player: PlayerForEnemy,
  camera: CameraForEnemy,
  runTimeSeconds: number,
  isBlocked?: (x: number, y: number, size: number) => boolean,
  ignoreMaxCount = false,
  atPos?: { x: number; y: number },
): EnemyInstance | null {
  const def = getEnemyDef(definitionId);
  if (!def) return null;

  // Check max active count (hordes bypass this)
  // BETA 20.04 — Was state.enemies.filter().length = full array alloc + scan
  // for a single count. Now a single pass with no allocation. At 200 enemies
  // × 30 spawn checks/sec = 6K filter allocs/sec eliminated.
  if (!ignoreMaxCount && def.maxActiveCount > 0) {
    let activeCount = 0;
    for (let i = 0; i < state.enemies.length; i++) {
      const e = state.enemies[i];
      if (e.alive && e.definitionId === definitionId) activeCount++;
    }
    if (activeCount >= def.maxActiveCount) return null;
  }

  // Use explicit position (shrine spawns) or pick one outside the viewport
  let pos = atPos ?? getSpawnPosition(player, camera);
  if (!atPos && isBlocked) {
    let attempts = 0;
    while (isBlocked(pos.x, pos.y, Math.min(def.hitboxRadius, PATHING_RADIUS_CAP)) && attempts < 8) {  // BETA 15.46 — pathing radius for spawn placement
      pos = getSpawnPosition(player, camera);
      attempts++;
    }
  }

  // Get scaled stats for current run time
  // BETA 19.98 — pass floor multiplier so floor 2 enemies actually scale 2× harder
  // than floor 1 (per Dylan: "floor 2 wont start with 1,000's of enemies. its another
  // build up just like the first 1 just 2 times harder."). Without this, the
  // state.time = 0 reset on floor advance made every floor's enemies as weak as
  // floor 1 minute 0. _currentFloorForScaling is updated each frame by enemyAdapter.
  const _floorMult = _currentFloorForScaling + 1;  // 0-indexed → display floor 1 = 1×, floor 2 = 2×, etc.
  // BETA 21.31 — Dylan: difficulty must CARRY FORWARD across floors. Farming to
  // 15:00 (5 min into ENDLESS) then advancing should bring 15-min-strength enemies
  // (HP + speed + aggro) onto floor 2 and keep climbing — NOT reset to the easy
  // pre-aggro state. So endless now runs on the GLOBAL clock too (the base ramp
  // already did). The floor-local reset (20.77) made floor 2+ secretly trivial.
  // The spawn-COUNT ramp (waveSystem floorStartTime) stays floor-local, so a new
  // floor still fills in GRADUALLY from empty — fresh-looking, but the enemies that
  // arrive are at full carried-forward strength. (_floorStartForScaling now unused.)
  void _floorStartForScaling;
  const scaled = getScaledStats(def, runTimeSeconds, variant, _floorMult, runTimeSeconds);

  // Determine initial facing direction toward player
  const facing = getFacingDirection(pos.x, pos.y, player.x, player.y, state.nextId);

  const instance: EnemyInstance = {
    id: state.nextId++,
    definitionId,
    variant,

    x: pos.x,
    y: pos.y,
    velocityX: 0,
    velocityY: 0,
    facing,

    // BETA 21.19 — god-mode multipliers fold in here so NEW spawns escalate; existing
    // enemies keep their spawn-time stats (no per-frame re-scale = no perf cost).
    currentHP: scaled.hp * (debugEnemyHpMult !== 1 ? debugEnemyHpMult : 1) * _godHpMult,
    maxHP:     scaled.hp * (debugEnemyHpMult !== 1 ? debugEnemyHpMult : 1) * _godHpMult,
    damage: scaled.damage * _godDmgMult,
    speed: scaled.speed * _godSpdMult,
    spriteSize: scaled.spriteSize,
    hitboxRadius: scaled.hitboxRadius,
    pathingRadius: scaled.pathingRadius,    // BETA 15.46 — movement-only collision radius (capped at 50)
    shieldHP: 0,
    maxShieldHP: 0,

    alive: true,
    // BETA 14.22 — start at 0 so newly spawned enemies are immediately eligible
    // to deal contact damage on first contact. Ticks down with dt each frame.
    contactCooldownRemaining: 0,
    // BETA 14.29 — retreat-from-trail bookkeeping. Both start at 0 (not
    // trailing, not retreating).
    trailingSince: 0,
    retreatUntil: 0,
    spawnTime: runTimeSeconds,
    flashTimer: 0,
    knockbackX: 0,
    knockbackY: 0,

    stuckTimer: 0,
    phaseTimer: 0,
    phaseMaxTimer: 0,
    wallPressTimer: 0,        // BETA 15.52 — wall-hop progress tracker
    lastDistToPlayer: 0,      // BETA 15.52 — populated on first frame
    jumpStartX: 0, jumpStartY: 0,    // BETA 15.63 — jump arc lerp start
    jumpTargetX: 0, jumpTargetY: 0,  // BETA 15.63 — jump arc lerp target

    navWaypointX: 0,
    navWaypointY: 0,
    navUpdateTimer: Math.random() * 0.5,
    navHoldRoom: false,
    navPath: [],

    spriteLoaded: false,
    currentSprite: getEnemySprite(def, facing, variant),

    // Stagger initial throw so a group of Timmys doesn't all throw at once
    throwTimer: def.throwAttack
      ? def.throwAttack.cooldown * (0.3 + Math.random() * 0.7)
      : 9999,
    throwWindupTimer: 0,
    throwTargetX: 0,
    throwTargetY: 0,

    slamTimer: def.slamAttack
      ? def.slamAttack.cooldown * (0.5 + Math.random() * 0.5)
      : 9999,
    slamPhase: 0,
    slamPhaseTimer: 0,
    slamTargetX: 0,
    slamTargetY: 0,

    eatTimer: def.eatAttack
      ? def.eatAttack.cooldown * (0.4 + Math.random() * 0.6)
      : 9999,
    eatPhase: 0,
    eatPhaseTimer: 0,
    eatTargetId: -1,

    lungeCooldown: 8 + Math.random() * 4,
    lungeUntil: 0,

    // BETA 13.95 — status effects
    frozenUntil: 0,
    slowedUntil: 0,
    slowMultiplier: 1.0,
    wetUntil: 0,
    // BETA 14.04 — Big Mac burn DoT
    burnUntil: 0,
    burnDps: 0,
    burnTickAccum: 0,
  };

  // Boss shield — for enemies with shieldPct > 0 in bossMultipliers
  if (variant === 'boss' && def.bossMultipliers && def.bossMultipliers.shieldPct > 0) {
    instance.maxShieldHP = Math.round(scaled.hp * def.bossMultipliers.shieldPct);
    instance.shieldHP = instance.maxShieldHP;
  }

  // Preload sprites (3-sprite system: up, left, right — no down)
  if (variant === 'elite' || variant === 'boss') {
    loadSprite(def.eliteSprite);
  } else {
    loadSprite(def.sprites.up);
    loadSprite(def.sprites.left);
    loadSprite(def.sprites.right);
  }
  // Preload attack sprites (all PNGs have built-in transparency)
  if (def.throwAttack) {
    loadSprite(def.throwAttack.throwSprite);
    loadSprite(def.throwAttack.projectileSprite);
  }
  if (def.slamAttack) {
    loadSprite(def.slamAttack.airborneSprite);
    loadSprite(def.slamAttack.crackSprite);
    // BETA 16.52 — preload the optional 4-frame mega-boss sprites
    if (def.slamAttack.divingSprite)  loadSprite(def.slamAttack.divingSprite);
    if (def.slamAttack.landingSprite) loadSprite(def.slamAttack.landingSprite);
  }
  if (def.eatAttack) {
    loadSprite(def.eatAttack.grabSprite);
    loadSprite(def.eatAttack.holdSprite);
    loadSprite(def.eatAttack.eatSprite);
  }

  state.enemies.push(instance);
  state.aliveCount++;
  return instance;
}

/**
 * Get a random spawn position outside the camera viewport.
 */
function getSpawnPosition(
  player: PlayerForEnemy,
  camera: CameraForEnemy,
): { x: number; y: number } {
  // BETA 14.42 — Pure 360° spawn ring (Vampire Survivors style). User:
  // "its like there is always a continuous amount of enemies always
  // spawning in 360 degrees at all times." Was: 70% biased toward player's
  // facing direction (forward arc only) + 30% random. Now: 100% random
  // around the full circle so pressure comes from all sides simultaneously.
  const angle = Math.random() * Math.PI * 2;
  const cosA  = Math.cos(angle);
  const sinA  = Math.sin(angle);

  // BETA 14.43 — Use the CURRENT camera.zoom (not BASE_SPAWN_ZOOM 1.0) so
  // spawn distance scales with the lab zoom-out slider. User: "back in
  // meaning outside the locked POV. so we dont see them just randomly
  // pop in out of nowhere." When user zooms out via sim panel, the
  // visible area gets larger; spawns now scale to always land just past
  // whatever the current visible edge is, so enemies never appear inside
  // the user's view.
  // 23.55 (audit) — floor at 0.5: far dev zoom (0.03) made the "just past
  // the visible edge" ring land 30k+ wu out — beyond despawn range, so
  // nothing ever spawned. Lab slider range keeps working (0.5+).
  const _zoomForSpawn = Math.max(0.5, camera.zoom > 0.01 ? camera.zoom : BASE_SPAWN_ZOOM);
  const halfW = (camera.viewportWidth  / _zoomForSpawn) / 2;
  const halfH = (camera.viewportHeight / _zoomForSpawn) / 2;

  // Distance to the viewport edge in this direction (ellipse-edge formula)
  const edgeDist = (halfW * halfH) /
    Math.sqrt(halfH * halfH * cosA * cosA + halfW * halfW * sinA * sinA);

  // Add a random buffer beyond the edge so enemies aren't immediately visible
  const offset = SPAWN_MARGIN + Math.random() * (SPAWN_MAX_DISTANCE - SPAWN_MARGIN);

  // BETA 13.79 — clamp inside the playable border. Bot pathfinding handoff
  // surfaced 25 events of zombies spawning at impossible positions (x as low
  // as -180, y as high as 10192 outside the playable area). Cause: when the
  // player kited near a border, the directional bias would project a spawn
  // beyond the world edge. Clamp keeps spawns just inside the border-wall
  // collision so they land on real ground instead of in the void.
  // 23.55 (audit) — the boundless world has no borders: clamping to the old
  // 0..10,000 box snapped every far-roam spawn to the box corner.
  if (BOUNDLESS_MAP) {
    return { x: player.x + cosA * (edgeDist + offset), y: player.y + sinA * (edgeDist + offset) };
  }
  const PAD = 32;
  const minX = BORDER_COLLISION_WEST  + PAD;
  const maxX = WORLD_SIZE - BORDER_COLLISION_EAST  - PAD;
  const minY = BORDER_COLLISION_NORTH + PAD;
  const maxY = WORLD_SIZE - BORDER_COLLISION_SOUTH - PAD;
  return {
    x: Math.max(minX, Math.min(maxX, player.x + cosA * (edgeDist + offset))),
    y: Math.max(minY, Math.min(maxY, player.y + sinA * (edgeDist + offset))),
  };
}

// ═══════════════════════════════════════════════════════════════
//  UPDATE (called every frame)
// ═══════════════════════════════════════════════════════════════

// BETA 23.6x — CO-OP PHASE 2 part C (§3.4): in-sim kill drops CARRY the killer's
// roster PlayerId when the kill site knows it (thorn-reflect kills = the shadowed
// contact player; damageEnemy kills = the threaded opts.sourcePlayerId). Optional
// — unstamped drops (enemy-eats-enemy devour, un-threaded callers) keep the legacy
// local-player scatter anchor in the adapter. Carry-only: the attribution LEDGER
// itself is Bram E4+ work; this type only guarantees the id is never lost.
export type AttributedEnemyLootDrop = EnemyLootDrop & { killerPlayerId?: string };

// BETA 23.6x — CO-OP PHASE 2 part C (§3): machine-local player id mirror for the
// burn/thorn attribution STAMPS on paths that don't (yet) thread a playerId —
// direct damageEnemy callers (weaponAdapter :434, weaponLab) convert with the
// weapon system in part D. Refreshed from gameState.localPlayerId every
// updateEnemies tick (data already flowing in — no new plumbing); 'p1' is the
// only local player in SP, so SP stamps are byte-stable. Identity plumbing, NOT
// a balance value.
let _localPlayerIdForAttribution: string = 'p1';

/**
 * Update all enemies: movement, collision, damage, cleanup.
 * Called once per frame from engine.ts update().
 *
 * @param dt - Delta time in seconds since last frame
 * @param runTimeSeconds - Total seconds elapsed in this run
 */
export function updateEnemies(
  state: EnemySystemState,
  dt: number,
  runTimeSeconds: number,
  // BETA 23.6x — CO-OP PHASE 2 part A: the sim now receives EVERY player's
  // live view (roster order, single-player = 1 element). Part A keeps all
  // internal consumption on the primary view below (byte-identical in SP);
  // part B converts targeting/velocity/geometry to nearest-of-N per
  // docs/design/coop-phase2-enemyadapter-plan.md §2.
  players: PlayerForEnemy[],
  camera: CameraForEnemy,
  isBlocked?: (x: number, y: number, size: number) => boolean,
  rooms?: PlacedRoom[],
  gameState?: GameState,
): AttributedEnemyLootDrop[] {
  const player = players[0];
  // BETA 23.6x — CO-OP PHASE 2 part C (§3): refresh the machine-local id mirror
  // (see _localPlayerIdForAttribution above). Lab harnesses that omit gameState
  // keep the 'p1' default — identical to SP.
  if (gameState?.localPlayerId) _localPlayerIdForAttribution = gameState.localPlayerId;
  const allDrops: AttributedEnemyLootDrop[] = [];

  for (let i = state.enemies.length - 1; i >= 0; i--) {
    const enemy = state.enemies[i];
    if (!enemy.alive) continue;
    // BETA 23.6x — CO-OP PHASE 2 part B: each enemy resolves its TARGET as
    // the nearest living player, and the rest of this loop (steering, nav,
    // facing, doorway magnet, retreat logic, contact damage, the slam/throw
    // helpers called below) consumes it through this shadowed binding.
    // Single-player: 1 view → identical to the old singular player.
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const player = nearestOfPlayers(players, enemy.x, enemy.y);

    const def = getEnemyDef(enemy.definitionId);
    if (!def) continue;

    // BETA 20.58 — Dylan: "elites disappear mid-attack / when they hit me." Root
    // cause: knockback (or warp/recycle) flings an enemy past DESPAWN_DISTANCE →
    // it's deleted that frame. Elites/bosses + the tanky "little boss" types
    // (Timmy, etc.) are rare + important, so NEVER auto-despawn them — not the
    // too-far cull here, not the stuck-despawn below.
    const _isImportant = enemy.variant === 'elite' || enemy.variant === 'boss'
      // 23.55 — Timmy deleted; "little boss" despawn protection now keys off
      // the brawler TAG so Phase C mini-bosses inherit it automatically
      || (ENEMY_DATABASE[enemy.definitionId]?.tags?.includes('brawler') ?? false);

    // ── Despawn if too far from player ──
    // BETA 23.6x — CO-OP PHASE 2 part C: `player` here is the loop-shadowed
    // NEAREST living view, so this cull (and the stuck-despawn's 400wu
    // combat guard below) only fires when the enemy is far from ALL living
    // players. SP: one view = the old primary math, unchanged.
    const distToPlayer = distance(enemy.x, enemy.y, player.x, player.y);
    if (distToPlayer > DESPAWN_DISTANCE && !_isImportant) {
      state.enemies.splice(i, 1);
      state.aliveCount = Math.max(0, state.aliveCount - 1);
      state.enemyDespawnsNoDeath = (state.enemyDespawnsNoDeath ?? 0) + 1; // BETA 20.58 — track non-death removals
      _enemyStuckTracking.delete(enemy.id);
      continue;
    }

    // ── BETA 14.82 — Fix 2: enemy pathfinding stuck recovery ──
    // PSBOT RUN cycle-11: large enemies got stuck in chad's
    // deadlock zone, couldn't reach him, sat invisibly off-screen for ~15min.
    // Sample each enemy's position every 1s. If an enemy moves <50wu in 5s
    // (5 consecutive stuck samples) → despawn so the next spawn can route
    // fresh. Skip for special-AI enemies (slam-mid-air, throw-windup, eating).
    //
    // BETA 15.6 — added IN-COMBAT-RANGE guard. Dylan: "every now and zombies
    // will be really close or or an enemy would be really close to me. And
    // then like the screen flickers or the zombie flickers, and they just
    // disappear. They're just gone." Bug: enemies in melee range orbit /
    // attack with tiny movements (<50wu/5s in tight contact) and were being
    // auto-despawned even though they were actively fighting. Now: enemies
    // within 400wu of the player skip the stuck-despawn entirely. The 15.3
    // A* pathfinding handles the original deadlock case for far enemies; the
    // 14.82 despawn only fires for ENEMIES STUCK FAR OFF-SCREEN now.
    {
      const _STUCK_SAMPLE_INTERVAL = 1.0;
      const _STUCK_MIN_DISTANCE = 50;
      const _STUCK_MAX_SAMPLES = 5;  // 5 samples × 1s = 5s
      const _STUCK_NEAR_PLAYER_GUARD = 400;  // BETA 15.6 — combat-range guard
      const distToPlayerSq = (enemy.x - player.x) ** 2 + (enemy.y - player.y) ** 2;
      const isNearPlayer = distToPlayerSq < _STUCK_NEAR_PLAYER_GUARD * _STUCK_NEAR_PLAYER_GUARD;
      const inSpecialAI =
        enemy.slamPhase > 0 ||
        enemy.throwWindupTimer > 0 ||
        enemy.eatPhase > 0 ||
        enemy.retreatUntil > runTimeSeconds ||
        enemy.phaseTimer > 0;
      if (!inSpecialAI && !isNearPlayer && !_isImportant) {
        const tracking = _enemyStuckTracking.get(enemy.id);
        if (!tracking) {
          _enemyStuckTracking.set(enemy.id, {
            x: enemy.x, y: enemy.y, lastSampleT: runTimeSeconds, stuckSamples: 0,
          });
        } else if (runTimeSeconds - tracking.lastSampleT >= _STUCK_SAMPLE_INTERVAL) {
          const movedDist = Math.hypot(enemy.x - tracking.x, enemy.y - tracking.y);
          if (movedDist < _STUCK_MIN_DISTANCE) {
            tracking.stuckSamples++;
            if (tracking.stuckSamples >= _STUCK_MAX_SAMPLES) {
              // Despawn — invisible deadlock would otherwise persist forever
              state.enemies.splice(i, 1);
              state.aliveCount = Math.max(0, state.aliveCount - 1);
              state.enemyDespawnsNoDeath = (state.enemyDespawnsNoDeath ?? 0) + 1; // BETA 20.58
              _enemyStuckTracking.delete(enemy.id);
              continue;
            }
          } else {
            tracking.stuckSamples = 0;  // reset — enemy is moving normally again
          }
          tracking.x = enemy.x;
          tracking.y = enemy.y;
          tracking.lastSampleT = runTimeSeconds;
        }
      } else if (isNearPlayer) {
        // Reset tracking when in combat range so an enemy re-engaging after
        // chasing through walls doesn't get despawned the moment they hit
        // melee range. Combat dance moves don't count toward "stuck".
        _enemyStuckTracking.delete(enemy.id);
      }
    }

    // ── Update hit flash timer ──
    if (enemy.flashTimer > 0) {
      enemy.flashTimer -= dt;
    }

    // ── BETA 14.22 — Tick down per-enemy contact cooldown ──
    // Decoupled from state.time so the lab time-lock can't freeze it. Without
    // this, in lab mode with a frozen runTimeSeconds, each enemy gets exactly
    // one hit on the player and is then silent forever (timeSinceLastDamage
    // stays at 0). User: "there are like 100 hundred enemies around me. just
    // not taking damage. ... i get hit like every 3 seconds maybe."
    if (enemy.contactCooldownRemaining > 0) {
      enemy.contactCooldownRemaining = Math.max(0, enemy.contactCooldownRemaining - dt);
    }

    // ── BETA 14.29 — Tick retreat timer + trailing accumulator ──
    // retreatUntil counts down while the enemy is in "peel-off" mode (visibly
    // running away from player). trailingSince counts UP while the enemy is
    // currently behind the moving player (used by the periodic retreat
    // trigger below the loop). User: "if there are 20 behind me for more
    // than 3 seconds half of them should run off screen."
    if (enemy.retreatUntil > 0) {
      enemy.retreatUntil = Math.max(0, enemy.retreatUntil - dt);
    }
    {
      // BETA 14.41 — Two new gates:
      // (1) attackModeOnly toggle skips retreat entirely (enemies just chase
      //     and attack). User: "these things are not even attacking me. its
      //     too focused on retreating."
      // (2) requiredPlayerVelMag gates trailingSince accumulation. Default
      //     80 so retreats only fire when player is meaningfully moving.
      //     User: "im standing still and enemies are running away... they
      //     should just be coming at me." Set slider to 0 for lab
      //     observation mode where retreats fire even when stationary.
      const _tuning = getRetreatTuning();
      if (!_tuning.attackModeOnly && enemy.retreatUntil <= 0) {
        // BETA 14.42 — Use movement grace window instead of instantaneous
        // smoothed velocity. Strafing back-and-forth makes smoothed
        // velocity dip toward 0 (left/right cancel) even though the player
        // is actively moving. Grace timer was refreshed earlier in the tick
        // any frame raw velocity exceeded 80wu/s.
        const isMoving = _movementGraceSec > 0 || _tuning.requiredPlayerVelMag === 0;
        // BETA 14.49 — Directional trailing gate. Previous logic accumulated
        // trailingSince for EVERY enemy whenever player was moving, regardless
        // of where the enemy stood relative to player movement direction. This
        // caused enemies the player was actively CHASING to qualify as trailing
        // and run away ("they are going around me when im running" — user).
        // Fix: dot product of (enemy - player) with player velocity. Negative
        // dot = enemy is in player's wake (truly trailing). Positive dot = enemy
        // is in front of where player is heading (player is chasing it; should
        // never retreat — enemy keeps beelining at player).
        const dxToEnemy = enemy.x - player.x;
        const dyToEnemy = enemy.y - player.y;
        const _tvTrail = velOf(player.playerId); // CO-OP part B: the TARGET's velocity
        const dotWithPlayerVel = dxToEnemy * _tvTrail.vx + dyToEnemy * _tvTrail.vy;
        const isActuallyTrailing = dotWithPlayerVel < 0;
        if (isMoving && isActuallyTrailing) {
          enemy.trailingSince += dt;
          if (enemy.trailingSince >= _tuning.trailingThresholdSec && Math.random() < _tuning.retreatProbPerSec * dt) {
            enemy.retreatUntil = _tuning.retreatDurationSec;
            enemy.trailingSince = 0;
            const retreatIdx = pushRetreatStart(
              enemy.definitionId,
              (enemy as { variant?: string }).variant ?? 'normal',
              runTimeSeconds, enemy.x, enemy.y,
            );
            (enemy as { _retreatEventIdx?: number })._retreatEventIdx = retreatIdx;
          }
        } else {
          // Player not moving fast enough OR enemy is in front of player's
          // direction of travel — reset trailing accumulator so chase-mode
          // enemies stay locked on no matter how fast the player runs at them.
          enemy.trailingSince = 0;
        }
      } else if (_tuning.attackModeOnly) {
        // Attack mode forces all enemies to chase/attack — clear any
        // lingering retreat state so they immediately resume chasing.
        enemy.trailingSince = 0;
        if (enemy.retreatUntil > 0) enemy.retreatUntil = 0;
      }
    }

    // ── Apply knockback (with wall collision) ──
    if (Math.abs(enemy.knockbackX) > KNOCKBACK_MIN || Math.abs(enemy.knockbackY) > KNOCKBACK_MIN) {
      const kbX = enemy.knockbackX * dt * 60;
      const kbY = enemy.knockbackY * dt * 60;
      if (isBlocked && !enemy.isFinalBoss) {
        // Try full move, then slide along axes
        // BETA 15.46 — pathingRadius for movement collision
        if (!isBlocked(enemy.x + kbX, enemy.y + kbY, enemy.pathingRadius)) {
          enemy.x += kbX;
          enemy.y += kbY;
        } else if (!isBlocked(enemy.x + kbX, enemy.y, enemy.pathingRadius)) {
          enemy.x += kbX;
        } else if (!isBlocked(enemy.x, enemy.y + kbY, enemy.pathingRadius)) {
          enemy.y += kbY;
        }
      } else {
        enemy.x += kbX;
        enemy.y += kbY;
      }
      enemy.knockbackX *= KNOCKBACK_FRICTION;
      enemy.knockbackY *= KNOCKBACK_FRICTION;
    } else {
      enemy.knockbackX = 0;
      enemy.knockbackY = 0;
    }

    // ── Stuck / phase timers ──
    if (enemy.phaseTimer > 0) enemy.phaseTimer -= dt;

    // ── BETA 15.63 — JUMP ARC LERP ──
    // While phasing (mid-jump), lerp position from jumpStart → jumpTarget
    // over the phaseTimer duration. Per Dylan: "are wea ble to make it so
    // we can see the zombie do the full jump? instead of teleport?" Yes —
    // position is interpolated each frame, so the sprite visibly TRAVELS
    // along an arc from one side of the wall to the other instead of
    // disappearing/reappearing. Combined with the climb-scale (1×→2.2×→1×)
    // and the render-side Y-lift in renderEnemies, this reads as a leap.
    // Skips other movement updates this frame (continue to next enemy).
    if (enemy.phaseTimer > 0 && enemy.phaseMaxTimer > 0) {
      const jumpProgress = 1 - enemy.phaseTimer / enemy.phaseMaxTimer; // 0→1
      // Smoothstep easing — slower at start + end, faster mid-air
      const eased = jumpProgress * jumpProgress * (3 - 2 * jumpProgress);
      enemy.x = enemy.jumpStartX + (enemy.jumpTargetX - enemy.jumpStartX) * eased;
      enemy.y = enemy.jumpStartY + (enemy.jumpTargetY - enemy.jumpStartY) * eased;
      // Update facing during the leap so sprite faces correctly mid-jump
      enemy.facing = getFacingDirection(enemy.x, enemy.y, player.x, player.y, enemy.id);
      enemy.currentSprite = getEnemySprite(def, enemy.facing, enemy.variant);
      continue; // skip movement / wall-collision / etc. for this enemy
    }

    // ── Room-aware nav waypoint ──
    if (rooms && rooms.length > 0) {
      enemy.navUpdateTimer -= dt;
      if (enemy.navUpdateTimer <= 0) {
        enemy.navUpdateTimer = 0.35 + (enemy.id % 7) * 0.04; // 0.35–0.59s, staggered by ID
        _updateNavWaypoint(enemy, rooms, player.x, player.y, gameState);
      }
      // Clear waypoint once enemy reaches the exit point past the gap
      if (enemy.navWaypointX !== 0 || enemy.navWaypointY !== 0) {
        const wpDx = enemy.navWaypointX - enemy.x;
        const wpDy = enemy.navWaypointY - enemy.y;
        if (wpDx * wpDx + wpDy * wpDy < _NAV_REACH_RADIUS_SQ) {
          // If following a multi-waypoint A* path, advance to next waypoint
          if (enemy.navPath.length > 1) {
            enemy.navPath.shift();
            enemy.navWaypointX = enemy.navPath[0].x;
            enemy.navWaypointY = enemy.navPath[0].y;
          } else {
            enemy.navPath = [];
            enemy.navWaypointX = 0;
            enemy.navWaypointY = 0;
          }
          // Short delay before recalc — gives the enemy a moment to move away from the gap
          // before checking which room it's in. Do NOT set to 0 (that causes oscillation).
          enemy.navUpdateTimer = Math.min(enemy.navUpdateTimer, 0.15);
        }
      }
    }

    // ── BETA 13.95: Status-effect timers tick down ──
    if (enemy.frozenUntil > 0) {
      enemy.frozenUntil -= dt;
      if (enemy.frozenUntil < 0) enemy.frozenUntil = 0;
    }
    if (enemy.slowedUntil > 0) {
      enemy.slowedUntil -= dt;
      if (enemy.slowedUntil <= 0) {
        enemy.slowedUntil = 0;
        enemy.slowMultiplier = 1.0;
      }
    }
    if (enemy.wetUntil > 0) {
      enemy.wetUntil -= dt;
      if (enemy.wetUntil < 0) enemy.wetUntil = 0;
    }
    // ── BETA 14.04 — Burn DoT (Big Mac Special Sauce) ──
    // Apply burnDps damage every 0.5s while burnUntil > 0. Damage attributed
    // to the source item (Big Mac) for telemetry.
    if (enemy.burnUntil > 0) {
      enemy.burnUntil -= dt;
      enemy.burnTickAccum += dt;
      if (enemy.burnTickAccum >= 0.5) {
        const tickDmg = Math.round(enemy.burnDps * 0.5);
        if (tickDmg > 0) {
          enemy.currentHP -= tickDmg;
          enemy.flashTimer = HIT_FLASH_DURATION;
          // BETA 22.74 — attribute to whatever set the burn (Big Mac item vs
          // flamethrower weapon). Only item_* sources feed item telemetry.
          const _burnSrc = enemy.burnSource ?? 'item_big_mac_special_sauce';
          if (_burnSrc.startsWith('item_')) _trackItemDamage(_burnSrc, tickDmg);
          if (enemy.currentHP <= 0 && enemy.alive) {
            // BETA 23.6x — CO-OP PHASE 2 part C (§3): burn kills still drop no loot
            // + skip kill credit — that's Bram E2's confirmed bug/fix (death-pipeline
            // surface is his). The attribution is NOT lost though: enemy.burnAppliedBy
            // (stamped where the burn was applied) carries the burning player's id
            // for E2 to consume. No logic change here.
            enemy.alive = false;
            state.totalKills++;
            state.aliveCount = Math.max(0, state.aliveCount - 1);
            // BETA 16.22 — death floor decal (Big Mac DoT path)
            spawnDeathDecal(enemy.definitionId, enemy.x, enemy.y, enemy.spriteSize);
            // BETA 20.72 — settling dust (DoT deaths get weight too)
            if (_deathDustCb) _deathDustCb(enemy.x, enemy.y, enemy.spriteSize);
    clearGasStateForEnemy(enemy.id);
          }
        }
        enemy.burnTickAccum = 0;
      }
      if (enemy.burnUntil <= 0) {
        enemy.burnUntil = 0;
        enemy.burnDps = 0;
        enemy.burnTickAccum = 0;
        enemy.burnSource = undefined;
        // BETA 23.6x — CO-OP PHASE 2 part C (§3): the stamp lives + dies with the burn.
        enemy.burnAppliedBy = undefined;
      }
    }

    // ── BETA 21.35 — FROZEN no longer LOCKS the enemy. Dylan: "just because an enemy
    // is frozen doesn't mean it doesn't move — it should just mean their attack is
    // slower." The old `continue` skipped ALL movement + attacks, so spamming Frozen
    // Darts / Ugly Kittens perma-locked the whole swarm and the player was untouchable
    // (THE immortality vector). Now frozen = heavy SLOW (movement ×0.45, applied in
    // speedMult below) + SLUGGISH attacks (attack ticks advance at 0.35× dt + a 2.5×
    // longer contact cooldown), so the swarm still closes in and can hurt you — just
    // slowly. Enemy still takes full damage + dies while frozen, as before.
    const _frozen = enemy.frozenUntil > 0;
    const _atkDt = _frozen ? dt * 0.35 : dt;

    // ── Eat attack tick (runs before slam/movement — it handles its own position) ──
    const isEating = def.eatAttack
      ? tickEatAttack(enemy, def.eatAttack, _atkDt, state.enemies, allDrops)
      : false;

    // ── Slam attack tick (suppressed while eating) ──
    const isSlamming = (!isEating && def.slamAttack)
      ? tickSlamAttack(enemy, def.slamAttack, _atkDt, player)
      : false;

    // ── Pack lunge tick (base zombies only, late-game) ──
    _tickPackLunge(enemy, state, runTimeSeconds, player, _atkDt);

    // ── Movement AI (with wall-aware slide) ──
    const oldX = enemy.x;
    const oldY = enemy.y;
    // BETA 15.52 — phaseTimer is now ONLY a visual sprite-grow effect (post-
    // teleport-jump landing animation). Wall collision STAYS active during
    // the 0.4s visual since the enemy is at a verified walkable position
    // (validated by jumpCandidates loop) and shouldn't clip nearby walls.
    // Removed `enemy.phaseTimer > 0` from the activeIsBlocked gate.
    const activeIsBlocked = (isSlamming || isEating || enemy.isFinalBoss) ? undefined : isBlocked;

    // ── Lunge speed boost (1.5×) — applied for the duration of this update only ──
    // BETA 13.95: slowed status multiplies into speed too. Net mult = lunge × slow.
    const lungeBoost = enemy.lungeUntil > 0 ? LUNGE_SPEED_MULT : 1.0;
    const slowBoost = enemy.slowedUntil > 0 ? enemy.slowMultiplier : 1.0;
    // BETA 20.65 — Bully Spray nearby-slow. 23.55: Timmy deleted — the item's
    // card copy already says "Brawlers", so the mechanic now keys off the
    // 'brawler' TAG and wakes up with the Phase C roster.
    let repelBoost = 1.0;
    if (ENEMY_DATABASE[enemy.definitionId]?.tags?.includes('brawler')) {
      const _inv = getItemState()?.inventory;
      if (_inv?.some(s => s.item.id === 'item_timmy_repellent_spray')) {
        const _rdx = player.x - enemy.x, _rdy = player.y - enemy.y;
        const _tRad = tuneOf('item_timmy_repellent_spray', 'slowRadius', 400); // BETA 21.79 — editable
        if (_rdx * _rdx + _rdy * _rdy < _tRad * _tRad) repelBoost = tuneOf('item_timmy_repellent_spray', 'slowMultiplier', 0.65);
      }
    }
    // BETA 21.35 — frozen = heavy movement slow (still moving, not locked).
    const frozenBoost = _frozen ? 0.45 : 1.0;
    const speedMult = lungeBoost * slowBoost * repelBoost * frozenBoost;
    const origSpeed = enemy.speed;
    if (speedMult !== 1.0) enemy.speed = origSpeed * speedMult;

    if (!isSlamming && !isEating) {
      // ── Doorway magnet (BETA 13.27, Manus) ──
      // If the straight-line path from enemy → player would clip a wall corner,
      // getDoorwayMagnet returns the doorway center to use as an immediate nav target.
      // Checked BEFORE the green-zone redirect.
      const magnet = rooms ? getDoorwayMagnet(rooms, enemy.x, enemy.y, player.x, player.y) : null;

      // ── Green-zone redirect (3-zone collision system, BETA 13.25) ──
      // If enemy is in a GREEN zone (interior corner curve or exterior guide curve),
      // steer toward the nearest doorway midpoint instead of stalling on the wall.
      const greenRedirect = !magnet && gameState ? getGreenZoneRedirect(gameState, enemy.x, enemy.y) : null;

      const overrideTarget = magnet ?? greenRedirect;
      if (overrideTarget) {
        const realWpX = enemy.navWaypointX;
        const realWpY = enemy.navWaypointY;
        enemy.navWaypointX = overrideTarget.x;
        enemy.navWaypointY = overrideTarget.y;
        updateMovement(enemy, def, dt, runTimeSeconds, player, activeIsBlocked);
        enemy.navWaypointX = realWpX;
        enemy.navWaypointY = realWpY;
      } else {
        updateMovement(enemy, def, dt, runTimeSeconds, player, activeIsBlocked);
      }
    }

    // ── Restore base speed (lunge boost only applied for this update) ──
    if (speedMult !== 1.0) enemy.speed = origSpeed;

    // ── BETA 15.61 — WALL COLLISION + WALL-HOP (FULL REWRITE PER DYLAN) ──
    // Per Dylan: "i think you have a lot of code contridicting old code…
    // please remove any nonsesne jumping mechanic code since its not working
    // and make it work using pathfinding. allow an aenmies pathfinding
    // hitbos to overlay like 2 percent od so on collision walls. they dont
    // bounce off of the wall. its smooth if they are touching the wall more
    // than 1 second they jump. right now enemies look like they are jirating
    // on the walls which wont work for this method."
    //
    // ROOT CAUSE OF THE JITTER (cycle of contradicting systems):
    //   1. steerTowardPlayer Layer 3 ±90° probes → tries perpendicular movement
    //   2. Slide-along-axis logic → succeeds, enemy moves sideways at full speed
    //   3. Wall-push system (PROBE +6 / PUSH 14) → repels them AWAY from wall
    //   4. Next frame, steering pulls them BACK toward wall
    //   5. Repeat forever → visual jitter, my dot-product timer reset by sideways slide
    //
    // NEW APPROACH (all prior wall-hop code DELETED):
    //   1. NO MORE slide-along-axis. If a frame's movement would clip a wall
    //      beyond the 2% overlap budget → revert that frame's move entirely.
    //      Enemy presses flush against wall, no perpendicular slide.
    //   2. NO MORE wall-push. Removed 14wu repulsion that fought steering.
    //   3. SIMPLE TOUCH TIMER. Each frame, oversized probe (radius × 1.10)
    //      detects "near wall." If touching, increment. If not, reset.
    //   4. 1 SECOND OF TOUCHING → JUMP. Wall-crossing scan for landing.
    //
    // navGraph A* still routes enemies through doorways (the right path).
    // When navGraph fails (player on other side of wall, no door routes
    // there) → enemy presses flush against wall → 1s touch → teleport jump.
    if (isBlocked && enemy.phaseTimer <= 0 && !enemy.isFinalBoss) {
      // ── 1. SOFT WALL COLLISION (allow 2% overlap, no slide, no bounce) ──
      // The enemy was already moved by steerTowardPlayer this frame. If their
      // new position is more than 2% inside a wall, revert this frame's move.
      // Otherwise allow the slight overlap — this is what makes contact "smooth"
      // instead of jittery.
      const SOFT_LIMIT = enemy.pathingRadius * 1.02; // 2% overlap allowed
      if (isBlocked(enemy.x, enemy.y, SOFT_LIMIT)) {
        enemy.x = oldX;
        enemy.y = oldY;
      }

      // ── 2. WALL-TOUCH DETECTION ──
      // Probe at 1.10× pathingRadius — slightly bigger than hitbox. If THIS
      // is blocked, the enemy is "touching" or "very close to" a wall.
      const TOUCH_PROBE = enemy.pathingRadius * 1.10;
      const touchingWall = isBlocked(enemy.x, enemy.y, TOUCH_PROBE);

      if (touchingWall) {
        enemy.wallPressTimer += dt;  // simple counter, no jitter to reset it
      } else {
        enemy.wallPressTimer = 0;
      }

      // ── 3. JUMP TRIGGER ──
      // BETA 21.45 — Dylan: "enemies take too long to jump walls. needs to be
      // .5 seconds in regular game and immediate in aggro mode with no delay."
      // Regular = 0.3s wall-press delay + 0.2s hop (below) ≈ 0.5s total. Aggro
      // (_endlessCcResist > 0) = 0 delay → jumps the instant it's wall-blocked.
      // Was a flat 1.0s of continuous contact (≈1.35s total) — felt sluggish.
      // BETA 21.51 — Dylan (floor 2): "enemies just bouncing/jumping everywhere."
      // 21.45's 0.3s was too eager — enemies hugging a wall while validly pathing
      // along it racked up the press timer and hopped constantly. 0.3 → 0.5 so they
      // only jump when genuinely stuck a beat (matches Dylan's original "0.5s" ask).
      // Aggro/endless still 0 (instant) — chaos there is expected.
      const _wallPressNeeded = _endlessCcResist > 0 ? 0 : 0.5;
      if (enemy.wallPressTimer >= _wallPressNeeded) {
        const dx = player.x - enemy.x;
        const dy = player.y - enemy.y;
        const distNow = Math.sqrt(dx * dx + dy * dy);

        if (distNow > 1) {
          const dirX = dx / distNow;
          const dirY = dy / distNow;

          // BETA 15.62 — Wall-crossing scan with TIGHT landing.
          const SCAN_STEP = 8;
          const SCAN_MAX = 350;
          let landed = false;
          let foundWall = false;
          let targetX = enemy.x;
          let targetY = enemy.y;
          for (let d = SCAN_STEP; d <= SCAN_MAX; d += SCAN_STEP) {
            const tx = enemy.x + dirX * d;
            const ty = enemy.y + dirY * d;
            const blocked = isBlocked(tx, ty, enemy.pathingRadius);
            if (blocked) {
              foundWall = true;
            } else if (foundWall) {
              targetX = tx;
              targetY = ty;
              landed = true;
              break;
            }
          }
          // Fallback (no wall in path but enemy stuck — knockback edge case)
          if (!landed && !foundWall) {
            const tx = enemy.x + dirX * 60;
            const ty = enemy.y + dirY * 60;
            if (!isBlocked(tx, ty, enemy.pathingRadius)) {
              targetX = tx;
              targetY = ty;
              landed = true;
            }
          }
          if (landed) {
            // BETA 15.63 — JUMP ARC LERP. Per Dylan: "are wea ble to make it
            // so we can see the zombie do the full jump? instead of
            // teleport?" Save start + target. Position lerps from start →
            // target during the phase animation (see lerp block at top of
            // movement section). Enemy is NOT moved instantly anymore —
            // the position update happens over 0.7s.
            enemy.jumpStartX = enemy.x;
            enemy.jumpStartY = enemy.y;
            enemy.jumpTargetX = targetX;
            enemy.jumpTargetY = targetY;
            // BETA 21.17 — Dylan: "make enemies jump the walls twice as fast."
            // 0.7 → 0.35s. BETA 21.33 — Dylan: "in aggro enemies should jump walls
            // immediately, maybe takes 0.2 seconds, and land quicker." In ENDLESS the
            // hop drops to 0.2s (snappier, lands faster). _endlessCcResist > 0 is the
            // aggro signal (set per-frame from the global clock, same as elsewhere).
            // (Contact damage has NO phase gate — they can already attack mid-jump.)
            // BETA 21.45 — regular hop 0.35→0.2 so total (0.3 press + 0.2 hop)
            // ≈ 0.5s per Dylan. Aggro hop 0.15 → near-instant after its 0-delay trigger.
            const _jumpDur = _endlessCcResist > 0 ? 0.15 : 0.2;
            enemy.phaseTimer = _jumpDur;
            enemy.phaseMaxTimer = _jumpDur;
            enemy.wallPressTimer = 0;
            enemy.stuckTimer = 0;
            enemy.navUpdateTimer = 0;
            playEnemyWallJump(enemy.x, enemy.y);
          } else {
            // No walkable landing — bleed timer to retry shortly.
            enemy.wallPressTimer = Math.max(0, enemy.wallPressTimer - 0.5);
          }
        } else {
          enemy.wallPressTimer = 0; // player on top of enemy
        }
      }
    } else if (enemy.phaseTimer <= 0) {
      // No isBlocked function or final boss — reset timer.
      enemy.wallPressTimer = 0;
    }

    // ── Update facing direction (3-sprite system: up, left, right) ──
    enemy.facing = getFacingDirection(enemy.x, enemy.y, player.x, player.y, enemy.id);
    enemy.currentSprite = getEnemySprite(def, enemy.facing, enemy.variant);

    // ── Throw attack (suppressed while eating or slamming) ──
    if (def.throwAttack && !isEating && !isSlamming) {
      tickThrowAttack(enemy, def.throwAttack, dt, player);
      if (enemy.throwWindupTimer > 0) {
        enemy.currentSprite = def.throwAttack.throwSprite;
      }
    }

    // ── Sprite overrides for slam and eat (ticks already ran before movement) ──
    if (isEating && def.eatAttack) {
      if (enemy.eatPhase === 1)      enemy.currentSprite = def.eatAttack.grabSprite;
      else if (enemy.eatPhase === 2) enemy.currentSprite = def.eatAttack.holdSprite;
      else if (enemy.eatPhase === 3) enemy.currentSprite = def.eatAttack.eatSprite;
    } else if (isSlamming && def.slamAttack) {
      const sa = def.slamAttack;
      if (enemy.slamPhase === 1) {
        // BETA 16.52 — 4-frame mega-boss anim. When divingSprite is
        // defined, swap to the descending frame once half
        // the airborne duration has elapsed (going up → coming down).
        // Regular slammers leave divingSprite undefined and stay on
        // airborneSprite the whole jump.
        const halfDur = sa.airborneDuration * 0.5;
        if (sa.divingSprite && enemy.slamPhaseTimer < halfDur) {
          enemy.currentSprite = sa.divingSprite;
        } else {
          enemy.currentSprite = sa.airborneSprite;
        }
      } else if (enemy.slamPhase === 2 && sa.landingSprite) {
        // BETA 16.52 — a mega boss shows a landing pose during phase 2.
        // Regular slammers (no landingSprite) revert to default sprite
        // since the crack overlay handles the floor cosmetic.
        enemy.currentSprite = sa.landingSprite;
      }
      // BETA 13.101 — Phase 2 (landing) no longer swaps the enemy sprite to
      // the crack overlay. The crack is now spawned as a separate persistent
      // ground effect (60s lifetime, under the enemy/player z-layer) when
      // the slam lands, so the crack stays visible on the floor instead of
      // the boss visually transforming into a crack for 0.55s. User: "floor
      // sprite for the slam jump + cracks needs to be under
      // enemies and bot and player but above floor. and they dont dissapear
      // for at least 60 seconds".
    }

    // ── Collision with player (touch damage) ──
    const touchDist = enemy.hitboxRadius + player.hitboxRadius;
    // BETA 14.30 (BUG-06) — REMOVED `player.iFrameTimer <= 0` from outer
    // gate. Previously, when player was in revive/Last-Stand iframe, the
    // damage callback never fired and _enemyAttacksMissedByIframe stayed
    // at zero. Now the callback fires every contact event; the inner
    // iframe check at enemyAdapter.ts:71 routes to MissedByIframe correctly.
    // contactCooldownRemaining still gates per-enemy attempt cadence so
    // we don't spam IFRAME++ 60×/sec per enemy during a 1.5s revive window.
    // BETA 14.82 — sprite-touch-no-damage tracking. User: "enemy sprites are
    // touching bot and no damage being dealt. that needs to 100% be tracked."
    // Sprite-touch radius = enemy.spriteSize/2 + player.spriteSize/2 (visual
    // overlap, looser than hitbox). Hitbox-touch already counted via the
    // existing damage callback's attempted++/missed buckets. NEW counters
    // measure the visual-vs-hitbox gap + cooldown-gated touches:
    //   _enemySpriteOverlapFrames    : frames where ANY enemy's sprite overlaps player's sprite
    //   _enemyTouchCooldownGated     : frames where enemy is in HITBOX touchDist but contactCooldownRemaining > 0
    //   _enemySpriteOnlyTouchFrames  : frames where sprite overlaps but NOT hitbox (sprite extends beyond hitbox)
    if (gameState) {
      const playerSprite = (player as { spriteSize?: number }).spriteSize ?? player.hitboxRadius * 2;
      const enemySprite = enemy.spriteSize ?? enemy.hitboxRadius * 2;
      const spriteTouchDist = (enemySprite + playerSprite) / 2;
      if (distToPlayer < spriteTouchDist) {
        const gs = gameState as unknown as Record<string, number | undefined>;
        gs._enemySpriteOverlapFrames = (gs._enemySpriteOverlapFrames ?? 0) + 1;
        if (distToPlayer >= touchDist) {
          // Sprite overlaps but hitbox doesn't — visual touch with no possible damage
          gs._enemySpriteOnlyTouchFrames = (gs._enemySpriteOnlyTouchFrames ?? 0) + 1;
        }
      }
      if (distToPlayer < touchDist && enemy.contactCooldownRemaining > 0) {
        // In hitbox touch range but cooldown is gating the attack
        const gs = gameState as unknown as Record<string, number | undefined>;
        gs._enemyTouchCooldownGated = (gs._enemyTouchCooldownGated ?? 0) + 1;
      }
    }
    if (distToPlayer < touchDist) {
      // Count an attack ATTEMPT only on an actual swing — i.e. when the
      // contact cooldown has cleared (the same gate the damage callback
      // below uses). Was incrementing every frame an enemy sat in touch
      // range with no cooldown gate, which massively over-counted (one
      // lingering enemy added ~60/sec). Gating here makes "attempted" match
      // the hit/miss buckets that only resolve when contactCooldownRemaining
      // <= 0. (The per-touch pressure signal lives in _enemyTouchCooldownGated
      // / _enemySpriteOnlyTouchFrames above.)
      if (gameState && enemy.contactCooldownRemaining <= 0) {
        const _gsAtt = gameState as unknown as Record<string, number | undefined>;
        _gsAtt._enemyAttacksAttemptedThisRun = (_gsAtt._enemyAttacksAttemptedThisRun ?? 0) + 1;
      }
      // BETA 14.36 — record re-contact time for retreat events. If this
      // enemy was warped post-retreat, capture how long it took to reach
      // the player from its warp position. First contact only.
      const _rIdx = (enemy as { _retreatEventIdx?: number })._retreatEventIdx;
      if (_rIdx !== undefined && _rIdx >= 0) {
        recordReContact(_rIdx, runTimeSeconds);
        (enemy as { _retreatEventIdx?: number })._retreatEventIdx = undefined; // consume so we don't double-record
      }
      if (enemy.contactCooldownRemaining <= 0) {
        const rawDamage = enemy.damage;
        // BETA 14.59 — set source for deathContext fatalHitSource tracking.
        // Uses the enemy's definition ID ('zombie', 'timmy', etc.)
        // so the analyst can attribute deaths to specific enemy types.
        setLastDamageSource(enemy.definitionId);
        // BETA 14.71 — BUG-1 from cycle-8 (explicit): pass 0 instead of
        // def.iFrameGrant. Per BETA 14.29, the player.damage() callback already
        // ignores the iFrameGrant param for normal contact damage (it's only
        // applied via the SAVE paths: shield-absorbed, Last Stand, Sheeesh,
        // Liar Deceiver, Bubble Wrap). Passing 0 makes the intent explicit at
        // the call site so future readers don't assume contact grants iframe.
        player.damage(rawDamage, 0, enemy.definitionId);
        // BETA 21.35 — frozen enemies attack SLOWER (2.5× longer cooldown), not never.
        enemy.contactCooldownRemaining = def.contactCooldown * (_frozen ? 2.5 : 1);

        // BETA 21.33 — Dylan: "enemies should hit you and you go flying. knockback,
        // and some enemies cause slow... the stronger the enemy the bigger the
        // effect." Every contact hit SHOVES the player away from the enemy, scaled
        // by that enemy's ATTACK strength (rawDamage) — a heavy slam flings
        // you far, a zombie just nudges; scales up automatically with ENDLESS since
        // rawDamage does. Per-hit force + accumulated shove are BOTH capped so a
        // dense swarm can't fling you off-map (you keep control; it decays fast in
        // the movement update). Timmy-type ALSO slows your move speed ~1s.
        {
          const _pp = player as unknown as { x: number; y: number; knockbackX?: number; knockbackY?: number; slowUntil?: number };
          const _kdx = _pp.x - enemy.x, _kdy = _pp.y - enemy.y;
          const _kd = Math.hypot(_kdx, _kdy) || 1;
          // BETA 21.36 — Dylan: "screen shakes when I get hit but I never SEE myself
          // knocked back." The 21.33 force (~92 for a zombie) was below the player's
          // 200/s run speed, so it was invisible while moving. Bumped so the shove
          // clearly out-paces movement: base 200 + 2.5×dmg, per-hit cap 900, accum 1000.
          // BETA 21.55 — Dylan: "I'm not seeing knockback when enemies hit me." Bumped
          // base 200→320 + 2.5→3.5×dmg, caps 900/1000 → 1300/1500, so a single hit
          // out-paces movement noticeably (it still cancels somewhat when a swarm hits
          // you from all sides at once — that's physics — but each clean hit now shoves).
          // BETA 21.70 — the base shove (was a hard-coded 320) is now per-enemy
          // (def.contactKnockback) so it's editable in the Balance dashboard and a
          // a heavy can be tuned to fling harder than a zombie. Defaults are
          // all 320 → identical to the old global behavior until someone retunes.
          // BETA 21.77 — Dylan amped contactKnockback in the dashboard but barely
          // moved: the force was HARD-capped at 1300 (and accum ±1500), so any value
          // above ~1300 did nothing. Now the cap SCALES with contactKnockback (×4),
          // so raising it in the dashboard actually flings you further. Default 320
          // keeps ≈ the old 1280/1500 ceiling. (In a swarm, opposite-side shoves
          // still cancel — that's physics, see the dashboard tooltip.)
          const _ckb = def.contactKnockback ?? 320;
          const _force = Math.min(_ckb * 4, _ckb + rawDamage * 3.5);   // per-hit shove; cap scales with the dashboard value
          const _accCap = Math.max(1500, _ckb * 4);
          _pp.knockbackX = Math.max(-_accCap, Math.min(_accCap, (_pp.knockbackX ?? 0) + (_kdx / _kd) * _force));
          _pp.knockbackY = Math.max(-_accCap, Math.min(_accCap, (_pp.knockbackY ?? 0) + (_kdy / _kd) * _force));
          // 23.55 — Timmy's contact-slow died with him; if Phase C brawlers
          // should slow on touch, gate it on the 'brawler' tag right here.
          // BETA 21.87 — Smashley's Bubble Wrap (R06): you're wrapped in bubbles, so
          // anything that touches you BOUNCES off. Fling the attacker away + cushion your
          // own shove. Dylan: "bubble wrap should have knockback on it as well. it only
          // makes sense." Direction (enemy - player) = -_kd vector, normalized.
          if (getItemState()?.inventory?.some(s => s.item.id === 'R06')) {
            enemy.knockbackX = (-_kdx / _kd) * 18;
            enemy.knockbackY = (-_kdy / _kd) * 18;
            _pp.knockbackX = (_pp.knockbackX ?? 0) * 0.5;  // the bubbles cushion YOUR fling
            _pp.knockbackY = (_pp.knockbackY ?? 0) * 0.5;
          }
        }

        // BETA 15.15 — N13 Not Xanax: slow the attacking enemy after a contact
        // hit lands. Per Dylan: "Enemies who hit you slow down. Anti-calm
        // aura." Slow duration = 1.0 + 0.05 × accumulatedBonus, max 3.0s.
        // Slow multiplier = 0.5 (50% speed). Multiple hits stack via existing
        // `slowedUntil = max(slowedUntil, newDuration)` mechanic so a fresh
        // hit refreshes the timer.
        if (gameState) {
          const _gs = gameState as unknown as { _notXanaxAccumulatedSnapshot?: number; _notXanaxSlowsAppliedThisRun?: number; _notXanaxTotalSlowSecondsThisRun?: number };
          const _xanaxAcc = _gs._notXanaxAccumulatedSnapshot ?? 0;
          if (_xanaxAcc > 0) {
            const _slowDur = Math.min(3.0, 1.0 + 0.05 * _xanaxAcc);
            // 23.55 (audit) — slowedUntil is a COUNTDOWN everywhere else;
            // adding runTimeSeconds made N13 slows last the whole run.
            const _slowEnd = _slowDur;
            // Refresh the slow timer if the new one is longer
            if (_slowEnd > enemy.slowedUntil) {
              enemy.slowedUntil = _slowEnd;
              enemy.slowMultiplier = 0.5;
            }
            // Track every application (refreshes count too — "anti-calm" intensity)
            _gs._notXanaxSlowsAppliedThisRun = (_gs._notXanaxSlowsAppliedThisRun ?? 0) + 1;
            _gs._notXanaxTotalSlowSecondsThisRun = (_gs._notXanaxTotalSlowSecondsThisRun ?? 0) + _slowDur;
          }
        }

        // Thorns: reflect damage back if hit actually landed (iFrameTimer > 0 means not dodged)
        const thorns = player.thorns ?? 0;
        if (thorns > 0 && player.iFrameTimer > 0) {
          const thornDmg = rawDamage * (thorns / 100);
          // BETA 23.6x — CO-OP PHASE 2 part C (§3): status stamp — WHOSE thorns are
          // chipping this enemy. `player` is the loop-shadowed contact target (the
          // thorny player), so the applying context knows the id; hand-built lab
          // views without a playerId fall back to the machine-local mirror. Carry
          // only — Bram's E2 death pipeline consumes it (thorn kills currently skip
          // elite credit + onPlayerKilledEnemy; his fix credits this player).
          enemy.thornsAppliedBy = player.playerId ?? _localPlayerIdForAttribution;
          enemy.currentHP -= thornDmg;
          enemy.flashTimer = HIT_FLASH_DURATION;
          if (enemy.currentHP <= 0 && enemy.alive) {
            enemy.alive = false;
            state.totalKills++;
            // BETA 23.6x — CO-OP PHASE 2 part C (§3.4): thorn-kill drops carry the
            // killer (the thorny player) so the adapter scatters them at THEIR feet.
            // Same single rollEnemyDrops call = identical RNG draw order; SP killer
            // is always p1 = state.player, so the scatter anchor is byte-identical.
            const _thornDrops: AttributedEnemyLootDrop[] = rollEnemyDrops(def, enemy.variant, 1.0);
            for (const _d of _thornDrops) _d.killerPlayerId = enemy.thornsAppliedBy;
            allDrops.push(..._thornDrops);
            // BETA 16.22 — death floor decal (thorns reflect path)
            spawnDeathDecal(enemy.definitionId, enemy.x, enemy.y, enemy.spriteSize);
            // BETA 20.72 — settling dust (thorns deaths get weight too)
            if (_deathDustCb) _deathDustCb(enemy.x, enemy.y, enemy.spriteSize);
    clearGasStateForEnemy(enemy.id);
          }
        }
      }
    }
  }

  // ── BETA 13.38 cycle 7 fix: hard boundary clamp on enemy positions ──
  // A* + doorway magnet + green redirect handle most pathing, but at high
  // enemy pressure (200-cap) some zombies still tunnel through walls and end
  // up out of bounds (e.g. y=-196). Once OOB they sit there spamming the
  // anomaly log every frame (cycle-7 had 5,656 enemy_oob events from ~15-30
  // actually escaped enemies). Catch-all: snap any enemy outside world bounds
  // back inside. Doesn't perfectly position them in a room, but stops the
  // OOB cascade and lets the enemy logic re-route from a valid position.
  // 23.55 (audit) — boundless world: there IS no out-of-bounds; the old snap
  // teleported every enemy to the 10,000-box edge when the player roamed
  // west/north of the origin (negative coords are normal ground now).
  if (!BOUNDLESS_MAP) {
    for (const enemy of state.enemies) {
      if (!enemy.alive) continue;
      if (enemy.x < 0 || enemy.x > WORLD_SIZE || enemy.y < 0 || enemy.y > WORLD_SIZE) {
        enemy.x = Math.max(0, Math.min(WORLD_SIZE, enemy.x));
        enemy.y = Math.max(0, Math.min(WORLD_SIZE, enemy.y));
        enemy.navUpdateTimer = 0; // force pathfinding refresh from clamped position
      }
    }
  }

  // ── Enemy-to-enemy separation (prevent stacking) ──
  // BETA 14.23 — pass player position so separation can DISABLE within the
  // ATTACK_ZONE_RADIUS, letting enemies stack on top of the player (and each
  // other when close to player) instead of forming a no-contact bubble.
  // BETA 23.6x — CO-OP PHASE 2 part D: full roster in — the stack-zone
  // exemption now rings EVERY living view (grid stays anchored to the
  // primary; see applySeparation header). SP: one view = old call exactly.
  applySeparation(state.enemies, players);

  // ── Update enemy projectiles ──
  // BETA 15.40 — pass gameState so projectiles can be killed on wall hit
  // (enemy projectiles previously passed through walls because tickProjectiles
  // had no way to query isWalkable). Per Dylan: "wepons should not be able to
  // shoot through walls or bounce off of walls."
  // BETA 23.6x — CO-OP PHASE 2 part D: full roster in — enemy projectiles
  // collision-test every living view (a throw sailing through p2 now lands),
  // first overlap in roster order consumes the projectile, same as before.
  tickProjectiles(players, dt, gameState);

  // ── Flank Warp: relocate lagging off-screen enemies ahead of player ──
  // Smoothed player velocity (world units/s) — CO-OP part B: every player's
  // velocity tracked in its own slot; grace stays keyed to the primary view
  // (retreat-gate is a world knob — part C decides its aggregate, OPEN).
  // BETA 23.6x — CO-OP PHASE 2 part C: the warp itself is per-anchor now —
  // eligibility protects enemies inside ANY living view's keep-alive box,
  // and relocation rings around each enemy's NEAREST living player. SP: one
  // view → the original primary-relative behavior, unchanged.
  if (dt > 0 && dt < 0.2) {
    const SMOOTH = 0.12;
    for (const pv of players) {
      const tv = velOf(pv.playerId);
      const rawVx = (pv.x - tv.lastX) / dt;
      const rawVy = (pv.y - tv.lastY) / dt;
      tv.vx = tv.vx * (1 - SMOOTH) + rawVx * SMOOTH;
      tv.vy = tv.vy * (1 - SMOOTH) + rawVy * SMOOTH;
      // BETA 14.42 — refresh movement grace whenever RAW velocity exceeds 80wu/s.
      // Smoothed avg can dip during strafe (left+right cancel) but raw spikes
      // every frame the player held a key. Grace decays in main update loop.
      if (pv === player) {
        const rawVelMag = Math.sqrt(rawVx * rawVx + rawVy * rawVy);
        if (rawVelMag >= 80) _movementGraceSec = 0.5;
        else _movementGraceSec = Math.max(0, _movementGraceSec - dt);
      }
    }
  }
  for (const pv of players) {
    const tv = velOf(pv.playerId);
    tv.lastX = pv.x;
    tv.lastY = pv.y;
  }

  _warpTimer -= dt;
  // BETA 14.37 — Flank Warp now fires regardless of player movement (was
  // gated on velMag > 80, which prevented warp/recycle when player stands
  // still in lab tests). When velMag is low, default normV to a random
  // direction so warp angles don't divide-by-zero on atan2.
  if (_warpTimer <= 0) {
    // BETA 14.38 — interval is runtime-tunable via retreat sim sliders.
    const _tuning = getRetreatTuning();
    _warpTimer = _tuning.warpIntervalSec;
    // BETA 23.6x — CO-OP PHASE 2 part C: warp geometry is anchored to each
    // enemy's NEAREST living player, so every view needs its own smoothed
    // travel direction (with the 14.37 idle-random fallback, per view).
    // SP: exactly one entry built from the primary's velocity — the identical
    // normVx/normVy math and the identical Math.random() draw order.
    const _warpDirs = players.map(pv => {
      const tv = velOf(pv.playerId);
      const m = Math.sqrt(tv.vx * tv.vx + tv.vy * tv.vy);
      return {
        nx: m > 0.01 ? tv.vx / m : Math.cos(Math.random() * Math.PI * 2),
        ny: m > 0.01 ? tv.vy / m : Math.sin(Math.random() * Math.PI * 2),
      };
    });

    // BETA 14.43 — Use current camera.zoom (not fixed BASE_SPAWN_ZOOM) so
    // warp respawn distance scales with sim zoom-out — enemies always
    // appear OUTSIDE the user's current view, never popping in inside.
    const _zoomForWarp = camera.zoom > 0.01 ? camera.zoom : BASE_SPAWN_ZOOM;
    const hwv = (camera.viewportWidth / 2) / _zoomForWarp;
    const hhv = (camera.viewportHeight / 2) / _zoomForWarp;

    // BETA 14.28 — Clump-warp eligibility relief. The original Flank Warp only
    // grabbed enemies that were both off-screen AND behind the player. In
    // dense hordes piled directly on the player, NO enemies satisfy that —
    // they're all on-screen. The horde then never recycles and the player
    // can't shake them. User: "the problem is they are grouped so close they
    // are not off screen so it would be smart for them to run the opposite
    // direction to get off screen depending on group size." Solution: any
    // enemy within CLUMP_RADIUS of the player is eligible regardless of
    // off-screen / behind-player checks. Tightly-clumped enemies recycle to
    // forward arc, breaking the swarm and creating real 360° pressure.
    const CLUMP_RADIUS = 80;
    const CLUMP_RADIUS2 = CLUMP_RADIUS * CLUMP_RADIUS;

    // BETA 23.6x — CO-OP PHASE 2 part C: an enemy inside ANY living player's
    // keep-alive box (viewport half-extents + SPAWN_MARGIN — the exact
    // pre-existing off-screen constants, no new tunables) is engaged with
    // someone and must NEVER be recycled. The two-view probe proved the old
    // primary-only test warped an enemy fighting 120wu from p2 onto the
    // primary's ring. Index 0 (the camera owner) is always consulted — extra
    // views are liveness-gated — so with 1 player this is byte-for-byte the
    // old single off-screen test.
    const _inAnyKeepAliveBox = (e: { x: number; y: number }): boolean => {
      for (let pi = 0; pi < players.length; pi++) {
        const pv = players[pi];
        if (pi > 0 && pv.currentHP <= 0) continue;
        if (Math.abs(e.x - pv.x) <= hwv + SPAWN_MARGIN && Math.abs(e.y - pv.y) <= hhv + SPAWN_MARGIN) return true;
      }
      return false;
    };

    const eligible: typeof state.enemies = [];
    for (const e of state.enemies) {
      if (!e.alive) continue;
      if (e.navHoldRoom) continue; // don't warp enemies frozen inside rooms
      // BETA 14.40 — REMOVED `runTimeSeconds - e.spawnTime < 8` alive-time
      // gate. runTimeSeconds is state.time, which gets FROZEN by lab time-
      // lock (same root cause as BETA 14.22 contact-cooldown + 14.37 trailing
      // detection bugs). With time locked, every enemy's aliveSec stayed at
      // 0, so the < 8 check filtered out EVERYTHING and the warp never fired
      // for any retreated/off-screen enemy. User: "basically none outside the
      // normal FOV ... never disappear and respawn." Now all alive non-
      // navHoldRoom enemies are eligible — the per-tick warpCountPerTick
      // slider caps how many actually warp each cycle.

      // BETA 23.6x — CO-OP PHASE 2 part C: all warp math is relative to the
      // enemy's nearest living player (its ANCHOR), not the primary camera
      // owner. SP: nearestOfPlayers fast-paths to players[0] — identical.
      const anchor = nearestOfPlayers(players, e.x, e.y);
      const ex = e.x - anchor.x;
      const ey = e.y - anchor.y;
      const dist2 = ex * ex + ey * ey;
      if (dist2 < 1) continue;
      const dist = Math.sqrt(dist2);

      // BETA 14.28 — Clumped-on-player enemies bypass the off-screen + behind
      // checks. They get to recycle directly from the swarm to forward arc.
      // (part C: "on-player" = on the enemy's NEAREST player.)
      const isClumped = dist2 < CLUMP_RADIUS2;

      // Must be off-screen (relaxed for clumped) — part C: of EVERY living view
      if (!isClumped && _inAnyKeepAliveBox(e)) continue;

      // Must be behind the player (relaxed for clumped — clump is everywhere
      // around player) — part C: behind the ANCHOR's travel direction
      const _dir = _warpDirs[players.indexOf(anchor)];
      if (!isClumped && (ex / dist) * _dir.nx + (ey / dist) * _dir.ny > -0.3) continue;

      eligible.push(e);
    }

    // BETA 14.40 — Warp count is now slider-controlled. Was floor 3 / ceil 20
    // / 20% of eligible. User: "a slider for how many respond, increase
    // enemies in respond slider." Cap at eligible.length so we don't try to
    // warp more than exist.
    const _tuneCount = getRetreatTuning();
    const warpCount = Math.min(_tuneCount.warpCountPerTick, eligible.length);
    // BETA 13.79 — same border clamp as getSpawnPosition (matches OOB-spawn fix).
    // 23.55 (audit) — boundless world: warp anywhere around the player
    const wMinX = BOUNDLESS_MAP ? -Infinity : BORDER_COLLISION_WEST  + 32;
    const wMaxX = BOUNDLESS_MAP ?  Infinity : WORLD_SIZE - BORDER_COLLISION_EAST  - 32;
    const wMinY = BOUNDLESS_MAP ? -Infinity : BORDER_COLLISION_NORTH + 32;
    const wMaxY = BOUNDLESS_MAP ?  Infinity : WORLD_SIZE - BORDER_COLLISION_SOUTH - 32;
    for (let i = 0; i < Math.min(warpCount, eligible.length); i++) {
      const e = eligible[i];
      // BETA 23.6x — CO-OP PHASE 2 part C: relocate around the enemy's
      // nearest living player (its pre-warp anchor — position unchanged
      // since the eligibility pass, so this resolves the same view), using
      // that view's travel direction. SP: the primary — unchanged math.
      const _target = nearestOfPlayers(players, e.x, e.y);
      const _tDir = _warpDirs[players.indexOf(_target)];
      // BETA 14.40 — Forward-bias percent is now slider-controlled. User:
      // "a slide for the randomness of where they respond at around the
      // FOV." 0 = pure random angle (max randomness, no clustering toward
      // forward direction). 100 = always forward arc (NWES pattern returns).
      // 60 = original 14.29 mix.
      const _tuneAngle = getRetreatTuning();
      const _forwardBias = _tuneAngle.warpForwardBiasPct / 100;
      const angle = Math.random() < _forwardBias
        ? Math.atan2(_tDir.ny, _tDir.nx) + (Math.random() - 0.5) * Math.PI
        : Math.random() * Math.PI * 2;
      // BETA 14.38 — spawn distance also runtime-tunable.
      const _tuneSpawn = getRetreatTuning();
      const dist2 = hwv + _tuneSpawn.spawnMarginWu + Math.random() * (_tuneSpawn.spawnMaxDistWu - _tuneSpawn.spawnMarginWu);
      e.x = Math.max(wMinX, Math.min(wMaxX, _target.x + Math.cos(angle) * dist2));
      e.y = Math.max(wMinY, Math.min(wMaxY, _target.y + Math.sin(angle) * dist2));
      e.stuckTimer = 0;
      e.phaseTimer = 0;
      e.phaseMaxTimer = 0;
      // BETA 14.36 — record warp-end on retreat events. The retreat-start
      // event was logged in the trailing-detection block above; here we
      // capture where the enemy actually landed + distance from player
      // (part C: from its warp anchor).
      const retreatIdx = (e as { _retreatEventIdx?: number })._retreatEventIdx;
      if (retreatIdx !== undefined && retreatIdx >= 0) {
        const wdx = e.x - _target.x, wdy = e.y - _target.y;
        recordWarpEnd(retreatIdx, runTimeSeconds, e.x, e.y, Math.sqrt(wdx * wdx + wdy * wdy));
        // _retreatEventIdx stays set so the per-enemy collision check (below)
        // can record the re-contact time when the warped enemy reaches the
        // player again.
      }
    }
  }

  return allDrops;
}

// ─────────────────────────────────────────────────────────────────────────────
//  PACK LUNGE (BETA 13.23)
//  Late-game zombies trigger 1s coordinated speed bursts in groups of 2-3.
//  Eligible only on base zombies (normal variant) after minute 5.
// ─────────────────────────────────────────────────────────────────────────────
const LUNGE_START_TIME      = 300;   // 5 minutes
const LUNGE_DURATION        = 1.0;   // seconds at +50% speed
const LUNGE_COOLDOWN_MIN    = 8.0;   // base cooldown after a lunge
const LUNGE_COOLDOWN_JITTER = 2.0;   // +0..2s jitter so packs don't sync forever
const LUNGE_RANGE_TO_PLAYER = 350;   // px — pack only triggers when player is close
const LUNGE_PACK_RANGE      = 120;   // px — neighbor must be within this to join lunge
const LUNGE_MAX_PACK        = 3;     // self + up to 2 buddies
const LUNGE_SPEED_MULT      = 1.5;

function _tickPackLunge(
  enemy: EnemyInstance,
  state: EnemySystemState,
  runTimeSeconds: number,
  player: PlayerForEnemy,
  dt: number,
): void {
  if (enemy.definitionId !== 'zombie' || enemy.variant !== 'normal') return;

  // Mid-lunge: count down, exit when done.
  if (enemy.lungeUntil > 0) {
    enemy.lungeUntil -= dt;
    if (enemy.lungeUntil < 0) enemy.lungeUntil = 0;
    return;
  }

  // Cooldown ticking — never trigger before 5:00 either.
  if (enemy.lungeCooldown > 0) {
    enemy.lungeCooldown -= dt;
    return;
  }
  if (runTimeSeconds < LUNGE_START_TIME) return;

  // Player must be in range.
  const dxp = player.x - enemy.x;
  const dyp = player.y - enemy.y;
  if (dxp * dxp + dyp * dyp > LUNGE_RANGE_TO_PLAYER * LUNGE_RANGE_TO_PLAYER) return;

  // Find pack buddies — also base zombies, not already lunging, within neighbor range.
  const packRangeSq = LUNGE_PACK_RANGE * LUNGE_PACK_RANGE;
  const buddies: EnemyInstance[] = [];
  for (const other of state.enemies) {
    if (other === enemy) continue;
    if (!other.alive) continue;
    if (other.definitionId !== 'zombie' || other.variant !== 'normal') continue;
    if (other.lungeUntil > 0) continue;
    const dx = other.x - enemy.x;
    const dy = other.y - enemy.y;
    if (dx * dx + dy * dy <= packRangeSq) {
      buddies.push(other);
      if (buddies.length >= LUNGE_MAX_PACK - 1) break;
    }
  }
  if (buddies.length === 0) return;

  // Trigger: self + buddies all enter LUNGING for 1s.
  enemy.lungeUntil    = LUNGE_DURATION;
  enemy.lungeCooldown = LUNGE_COOLDOWN_MIN + Math.random() * LUNGE_COOLDOWN_JITTER;
  for (const buddy of buddies) {
    buddy.lungeUntil    = LUNGE_DURATION;
    buddy.lungeCooldown = LUNGE_COOLDOWN_MIN + Math.random() * LUNGE_COOLDOWN_JITTER;
  }
}

/**
 * Movement AI for a single enemy.
 */
function updateMovement(
  enemy: EnemyInstance,
  def: EnemyDefinition,
  dt: number,
  runTimeSeconds: number,
  player: PlayerForEnemy,
  isBlocked?: (x: number, y: number, r: number) => boolean,
): void {
  // BETA 14.29 — Retreat override. While retreatUntil > 0, the enemy runs
  // AWAY from the player at full speed regardless of its base movement type.
  // Used by the periodic retreat-trigger to peel off long-trailers visibly,
  // letting them clear the screen so the next Flank Warp tick can recycle
  // them to the forward arc. User: "none in the giant group run away."
  if (enemy.retreatUntil > 0) {
    const dx = enemy.x - player.x;
    const dy = enemy.y - player.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > 1) {
      const dirX = dx / dist;
      const dirY = dy / dist;
      const moveX = dirX * enemy.speed * dt;
      const moveY = dirY * enemy.speed * dt;
      // Use the same wall-aware sliding pattern as steerTowardPlayer
      const newX = enemy.x + moveX;
      const newY = enemy.y + moveY;
      // BETA 15.46 — pathingRadius for retreat-mode movement collision
      if (!isBlocked || !isBlocked(newX, newY, enemy.pathingRadius)) {
        enemy.x = newX;
        enemy.y = newY;
      } else if (!isBlocked(newX, enemy.y, enemy.pathingRadius)) {
        enemy.x = newX;
      } else if (!isBlocked(enemy.x, newY, enemy.pathingRadius)) {
        enemy.y = newY;
      }
    }
    return; // Skip the regular movement switch this frame
  }

  // BETA 16.69 — Ugly Kittens monkey-bomb lure. After the retreat check
  // (fleeing enemies still flee the REAL player, not a kitten), check if
  // this enemy has been assigned to a kitten lure this frame. If yes,
  // shadow the player param so all the chase math below treats the
  // kitten as if it were the player. Single contact → existing
  // freeze+claw fires from weaponSystem; kitten persists for its 12s
  // lifetime (piercing 999) so it keeps clawing as more enemies pile in.
  const _lureTarget = getKittenLureTarget(enemy.id);
  if (_lureTarget) {
    player = { ...player, x: _lureTarget.x, y: _lureTarget.y };
  } else {
    // BETA 20.10 — trap lure (Asmon's Banana / Whoopie Cushion monkey bombs).
    // Same steer-override hook as kittens; kitten lure takes priority if both
    // are pulling this enemy (kitten check above runs first).
    const _trapLure = getTrapLureTarget(enemy.id);
    if (_trapLure) {
      player = { ...player, x: _trapLure.x, y: _trapLure.y };
    }
  }

  switch (def.movement) {
    case 'chase':
    case 'chase_fast': {
      const hasWp = enemy.navWaypointX !== 0 || enemy.navWaypointY !== 0;
      steerTowardPlayer(
        enemy,
        hasWp ? enemy.navWaypointX : player.x,
        hasWp ? enemy.navWaypointY : player.y,
        enemy.speed, dt, isBlocked,
      );
      break;
    }

    case 'intercept': {
      // BETA 14.28 — Predict where the player will be in 0.5s and aim there
      // instead of at their current position. _playerVx/_playerVy are
      // smoothed module-level velocities updated by the flank-warp tick at
      // the end of each enemy update (so they have last-frame's value here,
      // a 16ms lag that's imperceptible at 60fps). Falls back to chase
      // behavior when a navWaypoint is active (cross-room pathing) since
      // the waypoint is the actual path target, not the player.
      const PREDICT_SEC = 0.5;
      const hasWpI = enemy.navWaypointX !== 0 || enemy.navWaypointY !== 0;
      const _tvPredict = velOf(player.playerId); // CO-OP part B: predict the TARGET's path
      const targetX = hasWpI ? enemy.navWaypointX : player.x + _tvPredict.vx * PREDICT_SEC;
      const targetY = hasWpI ? enemy.navWaypointY : player.y + _tvPredict.vy * PREDICT_SEC;
      steerTowardPlayer(enemy, targetX, targetY, enemy.speed, dt, isBlocked);
      break;
    }

    case 'swarm': {
      // BETA 14.67 — Wobble REMOVED. User: "enemies are not bline to me still..
      // its like they are looking in random directions next to me." Wobble
      // (sin/cos × 18wu offset) was making swarm enemies target near the
      // player instead of AT the player, which read as "cautious" / weaving.
      // Pure Vampire Survivors style now: straight beeline at the player.
      const hasWpS = enemy.navWaypointX !== 0 || enemy.navWaypointY !== 0;
      steerTowardPlayer(
        enemy,
        hasWpS ? enemy.navWaypointX : player.x,
        hasWpS ? enemy.navWaypointY : player.y,
        enemy.speed, dt, isBlocked,
      );
      break;
    }

    case 'charge': {
      // BETA 14.67 — Pause-burst REMOVED. Was: 0.5s burst at 2.5× speed every
      // 3s, otherwise standing still. Made enemies look indecisive ("oh yeah
      // im supposed to attack you"). Now pure beeline like every other type.
      // If a "charge" enemy needs to feel different, it should be via base
      // speed/HP/damage, not by interrupting the chase loop.
      steerTowardPlayer(enemy, player.x, player.y, enemy.speed, dt, isBlocked);
      break;
    }

    case 'wander': {
      // BETA 14.67 — Wander phase REMOVED. Was: 3s of random movement,
      // then 2s of chase, repeating. Most "looking in random directions"
      // behavior the user complained about. Now pure beeline.
      steerTowardPlayer(enemy, player.x, player.y, enemy.speed, dt, isBlocked);
      break;
    }

    case 'orbit': {
      // BETA 14.67 — Orbit REMOVED. Was: circle player at 150wu radius
      // forever, never closing in. Worst offender for "not bee-lining."
      // Now: pure chase. Distance modifiers should come from base speed,
      // not from refusing to engage.
      steerTowardPlayer(enemy, player.x, player.y, enemy.speed, dt, isBlocked);
      break;
    }

    // BETA 14.71 — BUG-10 from cycle-8: 'flee_low_hp' case was identical
    // to default after BETA 14.67's pure-beeline conversion. PSBOT RUN
    // flagged it as dead code. Now removed; any enemy with the old
    // movement type 'flee_low_hp' falls through to default = pure chase.

    default: {
      steerTowardPlayer(enemy, player.x, player.y, enemy.speed, dt, isBlocked);
    }
  }
}

/**
 * Push enemies apart so they don't stack — spatial hash version.
 *
 * Old version: O(n²) — 300 enemies = 44,850 pair checks every frame = frame death.
 * New version: O(n) average via spatial grid — only checks enemies in adjacent cells.
 * Also runs every other frame; 30 Hz separation is visually indistinguishable from 60 Hz.
 *
 * BETA 14.23 — Inside ATTACK_ZONE_RADIUS of the player, separation is DISABLED
 * for affected pairs so enemies can actually stack on top of each other and reach
 * the player's hitbox. Without this, in dense hordes the back-pressure of outer
 * enemies pushed the inner (closest) enemies away from the player, creating an
 * invisible "no-contact bubble" — the player could be visually surrounded by 100
 * zombies but take zero damage because none could close to within touchDist.
 * User: "let enemies be able to get to me regardless if an enemy is near it or
 * on top of one other... at a certain radius of me enemies should be able to
 * stack on each other and there is no barrier. nothing blocking them from me
 * unless its walls and such... outside of that let them not be able to stack.
 * so it looks better in game."
 */
const ATTACK_ZONE_RADIUS  = 80;     // wu — inside this, enemies stack freely
const ATTACK_ZONE_RADIUS2 = ATTACK_ZONE_RADIUS * ATTACK_ZONE_RADIUS;
let _sepFrame = 0;
// BETA 23.6x — CO-OP PHASE 2 part D: takes the full players array. The GRID
// stays anchored to the primary view (cell keys are relative offsets only —
// neighbor lookup is pure key arithmetic, and the world-distance gate below
// decides every push, so a partner roaming far from the anchor degrades to
// extra distance checks, never wrong pushes — verified vs the 23.55 audit
// note). What goes per-player is the ATTACK-ZONE exemption: enemies may
// stack within ATTACK_ZONE_RADIUS of ANY living view, not just p1.
// Single-player: one view = the exact old math, byte-identical.
function applySeparation(enemies: EnemyInstance[], players: PlayerForEnemy[]): void {
  // Skip every other frame — halves cost with zero visible difference
  if (++_sepFrame % 2 !== 0) return;

  // BETA 23.6x — CO-OP PHASE 2 part D: grid anchor = primary view (see header
  // comment); zone views = living players (dead views grant no stacking
  // bubble). SP fast-path skips the filter — same single view, old behavior
  // exactly. All-downed MP roster falls back to the primary view, mirroring
  // nearestOfPlayers' never-undefined guarantee.
  const playerX = players[0].x;
  const playerY = players[0].y;
  let zoneViews: PlayerForEnemy[];
  if (players.length === 1) {
    zoneViews = players;
  } else {
    zoneViews = players.filter(pv => pv.currentHP > 0);
    if (zoneViews.length === 0) zoneViews = [players[0]];
  }

  const CELL   = SEPARATION_RADIUS * 2;  // 40 units per cell
  const RAD2   = SEPARATION_RADIUS * SEPARATION_RADIUS;
  // Key formula: cx*1000+cy — safe because max cx/cy ≈ 18000/40 = 450 < 1000
  const STRIDE = 1000;

  // Build grid over alive enemies only
  const grid = new Map<number, EnemyInstance[]>();
  // 23.55 (audit) — cells are PLAYER-RELATIVE now: absolute cells clamped
  // negative coords to 0 (every western enemy shared one bucket) and far
  // roams could overflow the cy<1000 key invariant. Enemies only exist
  // within despawn range of the player, so relative cells stay tiny.
  const pcx = Math.floor(playerX / CELL), pcy = Math.floor(playerY / CELL);
  for (let i = 0; i < enemies.length; i++) {
    const e = enemies[i];
    if (!e.alive) continue;
    const cx  = Math.floor(e.x / CELL) - pcx + 500;
    const cy  = Math.floor(e.y / CELL) - pcy + 500;
    const key = cx * STRIDE + cy;
    let cell  = grid.get(key);
    if (!cell) { cell = []; grid.set(key, cell); }
    cell.push(e);
  }

  // Check each cell vs itself + 4 forward neighbors (avoids double-counting pairs)
  // Offsets: same, right, bottom-left, bottom, bottom-right
  const DX = [0,  1, -1,  0,  1];
  const DY = [0,  0,  1,  1,  1];

  grid.forEach((cellA, key) => {
    const cx = Math.floor(key / STRIDE);
    const cy = key - cx * STRIDE;
    for (let ni = 0; ni < 5; ni++) {
      const ncx  = cx + DX[ni];
      const ncy  = cy + DY[ni];
      if (ncx < 0 || ncy < 0) continue;
      const nkey = ncx * STRIDE + ncy;
      const cellB = grid.get(nkey);
      if (!cellB) continue;
      const same = nkey === key;
      for (let i = 0; i < cellA.length; i++) {
        const a = cellA[i];
        // BETA 14.23 — inside attack zone of player, skip separation for this
        // enemy entirely so it (and anyone touching it) can stack on top of
        // the player's hitbox.
        // BETA 23.6x — CO-OP PHASE 2 part D: zone check runs against EVERY
        // living view (any player's stack bubble counts). SP: zoneViews has
        // exactly one entry — identical expressions, identical result.
        let aInZone = false;
        for (const pv of zoneViews) {
          const adxP = a.x - pv.x;
          const adyP = a.y - pv.y;
          if (adxP * adxP + adyP * adyP < ATTACK_ZONE_RADIUS2) { aInZone = true; break; }
        }
        for (let j = same ? i + 1 : 0; j < cellB.length; j++) {
          const b     = cellB[j];
          // Same zone check for b. If EITHER enemy is in the attack zone,
          // skip separation — prevents back-pressure from outer enemies
          // pushing inner enemies away from the player.
          if (aInZone) continue;
          // BETA 23.6x — CO-OP PHASE 2 part D: same any-living-view check for b.
          let bInZone = false;
          for (const pv of zoneViews) {
            const bdxP = b.x - pv.x;
            const bdyP = b.y - pv.y;
            if (bdxP * bdxP + bdyP * bdyP < ATTACK_ZONE_RADIUS2) { bInZone = true; break; }
          }
          if (bInZone) continue;
          const dx    = b.x - a.x;
          const dy    = b.y - a.y;
          const dist2 = dx * dx + dy * dy;
          if (dist2 >= RAD2 || dist2 < 0.0001) continue;
          const dist  = Math.sqrt(dist2);
          const push  = SEPARATION_FORCE * (1 - dist / SEPARATION_RADIUS) * 0.01;
          const px    = (dx / dist) * push;
          const py    = (dy / dist) * push;
          a.x -= px;  a.y -= py;
          b.x += px;  b.y += py;
        }
      }
    }
  });
}

// ═══════════════════════════════════════════════════════════════
//  DAMAGE & DEATH
// ═══════════════════════════════════════════════════════════════

/**
 * BETA 13.97 — Strip shieldHP and/or armor from an enemy by id.
 * Used by Ultra Beam (def.shieldStripPerHit / def.armorStripPerHit) on each
 * aura tick that touches the enemy. Floors at 0; doesn't damage HP directly.
 */
export function applyEnemyArmorShieldStrip(
  state: EnemySystemState,
  enemyId: number,
  shieldStrip: number,
  armorStrip: number,
): void {
  const enemy = _findEnemyById(state, enemyId);   // BETA 20.04 — O(1) Map cache (was O(N) array.find)
  if (!enemy || !enemy.alive) return;
  if (shieldStrip > 0 && enemy.shieldHP > 0) {
    enemy.shieldHP = Math.max(0, enemy.shieldHP - shieldStrip);
  }
  // Note: enemy "armor" isn't a current EnemyInstance field; bossMultipliers
  // sets shieldPct only. We keep the param for forward-compat but no-op for now.
  void armorStrip;
}

/**
 * BETA 13.95 — Apply a status effect to an enemy by id.
 * Used by the weapon system to apply FREEZE / SLOW / WET on hit.
 * Stacking rule: longer of (current timer, new duration).
 */
export function applyEnemyStatus(
  state: EnemySystemState,
  enemyId: number,
  effect: 'freeze' | 'slow' | 'wet',
  duration: number,
  slowMultiplier: number = 0.5,
): void {
  const enemy = _findEnemyById(state, enemyId);   // BETA 20.04 — O(1) Map cache (was O(N) array.find)
  if (!enemy || !enemy.alive) return;
  // BETA 20.03 — Dylan: "slow attacks should be less effective on bosses and
  // elite enemies. by like 50 percent." Halve duration for elites/bosses so
  // freeze/slow/wet status effects don't trivialize the heavy-tier threats.
  // Mega bosses and crowned mobs should still be REAL fights — perma-frozen
  // bosses are not the design goal. Normal-variant enemies unaffected.
  const _slowResistMult = (enemy.variant === 'elite' || enemy.variant === 'boss') ? 0.5 : 1.0;
  // BETA 21.31 — ENDLESS CC resistance also cuts slow/freeze/wet DURATION so the
  // swarm shakes off crowd-control faster late-game and keeps closing in (0.7 resist
  // = effects last 30% as long). Stacks with the elite/boss halving above.
  const _adjDuration = duration * _slowResistMult * (1 - _endlessCcResist);
  if (effect === 'freeze') {
    if (_adjDuration > enemy.frozenUntil) enemy.frozenUntil = _adjDuration;
  } else if (effect === 'slow') {
    if (_adjDuration > enemy.slowedUntil) enemy.slowedUntil = _adjDuration;
    enemy.slowMultiplier = slowMultiplier;
  } else if (effect === 'wet') {
    if (_adjDuration > enemy.wetUntil) enemy.wetUntil = _adjDuration;
  }
}

/**
 * Deal damage to an enemy. Returns loot drops if the enemy dies.
 * Called by the weapon/projectile system when a projectile hits an enemy.
 *
 * @param knockbackForce - Knockback strength (0 = none)
 * @param knockbackAngle - Direction of knockback in radians
 * @param luckMultiplier - Player's luck stat for drop chances
 */
export function damageEnemy(
  state: EnemySystemState,
  enemyId: number,
  amount: number,
  knockbackForce: number = 0,
  knockbackAngle: number = 0,
  luckMultiplier: number = 1.0,
  sourceItemId?: string,
  // BETA 23.6x — CO-OP PHASE 2 part C (§3): options-object TAIL per Bram's E2
  // signature courtesy (his damageType registry param slots in here later —
  // positional args never reorder). sourcePlayerId = roster id of the attacking
  // player; the adapter wrapper + AoE-drain paths always thread it (defaulted to
  // the machine-local player), the weapon path (weaponAdapter :434) threads it
  // when the weapon system converts (part D sibling task).
  opts?: { sourcePlayerId?: string },
): { killed: boolean; drops: AttributedEnemyLootDrop[] } {
  const enemy = _findEnemyById(state, enemyId);   // BETA 20.04 — O(1) Map cache (was O(N) array.find)
  if (!enemy || !enemy.alive) {
    return { killed: false, drops: [] };
  }

  const def = getEnemyDef(enemy.definitionId);
  if (!def) return { killed: false, drops: [] };

  // BETA 14.04 — Stompatine perc (N11): +8% damage per pick vs named elites
  // (mini-boss definitionIds). Multiplier applied at the top of damage so
  // it stacks with other modifiers cleanly. Percs slot lookup is cheap (≤4 slots).
  {
    // BETA 20.01 — Stompatine NOW actually hits elites + bosses (the 19.98
    // CHANGELOG claimed this but only updated the description). Fixed: extends
    // beyond named definitionIds to ALL `variant === 'elite'` (crowned mobs)
    // AND `variant === 'boss'` (future mega bosses). Per Dylan's
    // "boss-stomping juice" intent. Named-elite check kept for backward compat
    // (mini-boss enemies that aren't variant-tagged as elite/boss).
    const isStompTarget = (ENEMY_DATABASE[enemy.definitionId]?.tags?.includes('brawler') ?? false)   // 23.55 — was definitionId === 'timmy'
      || enemy.variant === 'elite'
      || enemy.variant === 'boss';
    if (isStompTarget) {
      const ps = getPercState();
      if (ps) {
        const stompSlot = ps.slots.find(s => s.perc?.id === 'N11' && s.level > 0);
        if (stompSlot && stompSlot.perc) {
          const bonusPct = stompSlot.perc.bonusPerLevel * stompSlot.level / 100;
          amount *= (1 + bonusPct);
          // BETA 14.62 — N11 conditionMetFrames hook. Each named-elite damage
          // event with N11 equipped counts as one "condition met" — analyst
          // can correlate vs total named-elite hits to gauge proc density.
          trackPercCondition('N11');
          // BETA 14.62 — Synergy: timmyRepellent_stompatine fires when
          // damaging a named elite with both N11 + Timmy Repellent equipped.
          // valueDelta = bonus damage applied (the stomp boost amount).
          const itemState = getItemState();
          const hasTimmyRepel = itemState?.inventory.some(s => s.item.id === 'item_timmy_repellent_spray');
          if (hasTimmyRepel) {
            const bonusDmg = amount - amount / (1 + bonusPct);
            trackSynergyTrigger('timmyRepellent_stompatine', bonusDmg);
          }
        }
      }
    }
  }

  // Apply damage — shield absorbs first (boss mechanic)
  let remainingDmg = amount;
  if (enemy.shieldHP > 0) {
    const shieldAbsorb = Math.min(enemy.shieldHP, remainingDmg);
    enemy.shieldHP -= shieldAbsorb;
    remainingDmg -= shieldAbsorb;
  }
  enemy.currentHP -= remainingDmg;
  enemy.flashTimer = HIT_FLASH_DURATION;

  // Per-item telemetry (BETA 13.23): track AoE damage by source item ID.
  if (sourceItemId) _trackItemDamage(sourceItemId, amount);

  // ── BETA 14.04 — On-hit item effects ──
  // Big Mac Special Sauce (rare): 25% chance × stacks to apply BURN DoT
  // (8 dmg/s for 4s + 1.5s/extra-stack — BETA 19.97 base 3 → 8). Stay Frosty
  // (common): apply SLOW (50%) for 1.5s + 0.5s/extra-stack. Both check live inventory.
  if (enemy.alive && enemy.currentHP > 0) {
    const inv = getItemState()?.inventory;
    if (inv) {
      const bigMacSlot = inv.find(s => s.item.id === 'item_big_mac_special_sauce');
      if (bigMacSlot) {
        // BETA 14.12 — Big Mac scales with player stats:
        //   proc chance: base 25% + luck bonus (1 luck = +1% proc chance)
        //   burn duration: base 4s + 1.5s/extra-stack, scales by attackAreaBonus
        //   burn DPS: base 8 (BETA 19.97: 3 → 8 per Dylan audit, 12 total dmg per burn was invisible vs floor-3+ enemy HP), scales by damageBonus
        // BETA 21.75 — base magnitudes now editable via tuneOf (Balance Dashboard);
        // the luck/stack/area/damage scaling math is unchanged. Defaults = old literals.
        const _bmId = 'item_big_mac_special_sauce';
        const baseChance = tuneOf(_bmId, 'procChance', 0.25) + Math.min(tuneOf(_bmId, 'luckChanceCap', 0.5), _cachedPlayerStats.luck / 100);
        const stackChance = 1 - Math.pow(1 - baseChance, bigMacSlot.stacks);
        if (rollRng() < stackChance) { // BETA 20.94 — seeded Big Mac burn proc
          const burnDuration = (tuneOf(_bmId, 'burnDuration', 4) + (bigMacSlot.stacks - 1) * tuneOf(_bmId, 'burnDurationPerStack', 1.5))
            * (1 + _cachedPlayerStats.attackAreaBonus / 100);
          // BETA 20.03 — Big Mac burn DoT also nerfed 50% vs elite/boss per
          // Dylan's slow-attacks rule. Burn DPS scales with player damage but
          // gets halved against heavy tiers.
          const _burnResistMult = (enemy.variant === 'elite' || enemy.variant === 'boss') ? tuneOf(_bmId, 'eliteBossMult', 0.5) : 1.0;
          enemy.burnUntil = Math.max(enemy.burnUntil, burnDuration * _burnResistMult);
          enemy.burnDps = tuneOf(_bmId, 'burnDps', 8) * (1 + _cachedPlayerStats.damageBonus / 100) * _burnResistMult;
          enemy.burnSource = _bmId; // BETA 22.74 — explicit attribution (flame burn may coexist)
          // BETA 23.6x — CO-OP PHASE 2 part C (§3): status stamp — WHO applied this
          // burn. Threaded id when the caller passed one (adapter wrapper / AoE
          // drain); direct callers that can't know yet (weaponAdapter :434 — the
          // weapon system converts in part D) fall back to the machine-local mirror
          // ('p1' in SP, so byte-stable). Carry-only for Bram's E2 burn-kill fix.
          enemy.burnAppliedBy = opts?.sourcePlayerId ?? _localPlayerIdForAttribution;
          enemy.burnTickAccum = 0;
        }
      }
      const frostySlot = inv.find(s => s.item.id === 'item_stay_frosty');
      if (frostySlot) {
        // BETA 14.12 — Stay Frosty slow duration scales with attackAreaBonus
        // (debuff longevity). Multiplier stays 0.5 (50% slow flat).
        // BETA 21.75 — base magnitudes editable via tuneOf; area scaling unchanged.
        const slowDuration = (tuneOf('item_stay_frosty', 'slowDuration', 1.5) + (frostySlot.stacks - 1) * tuneOf('item_stay_frosty', 'slowDurationPerStack', 0.5))
          * (1 + _cachedPlayerStats.attackAreaBonus / 100);
        enemy.slowedUntil = Math.max(enemy.slowedUntil, slowDuration);
        enemy.slowMultiplier = tuneOf('item_stay_frosty', 'slowMultiplier', 0.5);
        // BETA 14.61 — Stay Frosty proc tracker
        trackItemProc('item_stay_frosty');
        // BETA 14.61 — Synergy: frozenDarts_stayFrosty fires when enemy
        // is being slowed by Frosty AND Frozen Darts is also equipped.
        // valueDelta tracks total slow-frame uplift for analyst.
        const hasFrozenDarts = inv.some(s => s.item.id === 'wpn_frozen_darts')
          || _cachedPlayerStats; // weapon equipment check — simpler heuristic OK
        if (hasFrozenDarts) {
          trackSynergyTrigger('frozenDarts_stayFrosty', slowDuration);
        }
        // BETA 14.61 — Synergy: bigMacSauce_stayFrosty fires when both equipped.
        const hasBigMac = inv.some(s => s.item.id === 'item_big_mac_special_sauce');
        if (hasBigMac) {
          trackSynergyTrigger('bigMacSauce_stayFrosty', slowDuration);
        }
        // BETA 14.61 — Synergy: squirter_frozenDarts (wet enemies freeze longer)
        // fires when both equipped. Approximated via slowDuration.
        const hasSquirter = inv.some(s => s.item.id === 'wpn_squirter');
        if (hasSquirter && hasFrozenDarts) {
          trackSynergyTrigger('squirter_frozenDarts', slowDuration);
        }
      }
    }
  }
  // ── End BETA 14.04 on-hit ──

  // Apply knockback — resistance scales with enemy variant
  if (knockbackForce > 0) {
    // BETA 21.71 — clamp to the documented 0..1 range. A value >1 (e.g. a
    // dashboard edit of knockbackResist=5) made effectiveForce = force×(1−resist)
    // go NEGATIVE, yanking the enemy TOWARD the player on every hit. Clamp so an
    // out-of-range edit reads as "fully immovable" instead of inverting physics.
    let resist = Math.max(0, Math.min(1, def.knockbackResist));
    if (enemy.variant === 'boss') {
      // Boss: use explicit bossMultipliers.knockbackResist, or cap base + 0.12.
      // BETA 21.78 — base off the already-CLAMPED `resist` (not raw def.knockbackResist)
      // so a negative dashboard value can't push boss/elite resist negative (inverting it).
      resist = def.bossMultipliers?.knockbackResist ?? Math.min(0.97, resist + 0.12);
    } else if (enemy.variant === 'elite') {
      // Elite: 10% more resistant than normal — tougher to crowd-control.
      resist = Math.min(0.93, resist + 0.10);   // BETA 21.78 — clamped base (see above)
    }
    // BETA 21.31 — stack ENDLESS CC resistance on top of the variant resist so the
    // swarm pushes through knockback in deep runs (a zombie at 0 base resist + 0.7
    // endless = 0.7 effective → only 30% of the shove lands).
    const _effResist = resist + (1 - resist) * _endlessCcResist;
    const effectiveForce = knockbackForce * (1 - _effResist);
    // BETA 22.03 — Dylan: "some enemies still randomly disappear." ROOT CAUSE: enemy knockback
    // was UNCAPPED here. Stacked hits in one frame (multi-projectile volley, aura ticks) pile
    // knockbackX/Y huge; the move loop flings the enemy `knockback × 6.7` world-units total
    // (kbX = knockback·dt·60, friction 0.85/frame → Σ = 1/0.15), so a big stack rockets it past
    // DESPAWN_DISTANCE (2500) and the too-far cull deletes it mid-fight with NO loot (only
    // regular enemies — elites are cull-exempt, matching the report). Cap the accumulated
    // knockback so the max total fling (~ENEMY_KNOCKBACK_CAP·6.7 ≈ 1330wu) stays well under the
    // cull line. Normal single hits (knockback ~5-20 → ~33-130wu) are nowhere near the cap, so
    // knockback feel is unchanged; only pathological stacking is clamped.
    enemy.knockbackX = Math.max(-ENEMY_KNOCKBACK_CAP, Math.min(ENEMY_KNOCKBACK_CAP, enemy.knockbackX + Math.cos(knockbackAngle) * effectiveForce));
    enemy.knockbackY = Math.max(-ENEMY_KNOCKBACK_CAP, Math.min(ENEMY_KNOCKBACK_CAP, enemy.knockbackY + Math.sin(knockbackAngle) * effectiveForce));
  }

  // Check death
  if (enemy.currentHP <= 0) {
    enemy.alive = false;
    state.totalKills++;
    state.aliveCount = Math.max(0, state.aliveCount - 1);

    // BETA 16.22 — Per Dylan: enemy death floor sprite. Spawns at the
    // enemy's death position with the SAME spriteSize as alive enemy
    // (no huge size jump). Module silently no-ops for enemies without a
    // registered death sprite (kevin / viewbotter / etc.).
    spawnDeathDecal(enemy.definitionId, enemy.x, enemy.y, enemy.spriteSize);
    // BETA 20.72 — settling dust so the body lands with weight (engine spawns motes)
    if (_deathDustCb) _deathDustCb(enemy.x, enemy.y, enemy.spriteSize);
    clearGasStateForEnemy(enemy.id);

    // BETA 20.63 — count each crowned/boss kill ONCE. Was triple-counted
    // (elite + boss + every named-mini-boss-by-id all incremented), inflating
    // bossEliteKills ~3× and conflating "is an elite" with "is a Timmy". A kill
    // is an elite iff its VARIANT is elite/boss — a normal named mini-boss is NOT.
    if (enemy.variant === 'elite' || enemy.variant === 'boss') {
      state.totalEliteKills++;
    }
    // audit — per-type kill volume (verify the flood fix)
    if (enemy.variant === 'elite') __chestAudit.killsEliteVariant++;
    __chestAudit.killsTotal++;

    // Roll loot
    const drops: AttributedEnemyLootDrop[] = rollEnemyDrops(def, enemy.variant, luckMultiplier);
    // BETA 23.6x — CO-OP PHASE 2 part C (§3): kill drops carry the attacker's
    // roster id whenever it was threaded in — never lost on the way to the
    // consumers (adapter wrapper / AoE drain / weaponAdapter). Un-threaded
    // callers leave drops unstamped = legacy behavior. Ledger = Bram E4+.
    if (opts?.sourcePlayerId) {
      for (const _d of drops) _d.killerPlayerId = opts.sourcePlayerId;
    }

    // BETA 20.56 — Blue Elite Chest: drops every 10th elite kill (was EVERY
    // elite in 20.49, which flooded — a floor-1 10-min run has ~150 elites =
    // ~150 chests). Per Dylan: "every 10 elites killed, one drops" → ~15
    // chests/run. The engine drains pendingEliteChestSpawns each frame → pushes
    // a blue Chest (higher-end loot mix) into roomMap.chests.
    if (enemy.variant === 'elite') {
      state.firstEliteKilledThisFloor = true;
      state.eliteChestCounter = (state.eliteChestCounter ?? 0) + 1;
      // BETA 20.77 — TIME THROTTLE on top of the every-10 gate (god-mode
      // audit: Dylan's 8,636-kill run produced ~600 elites = ~60 free
      // legendary chests; the gate was sized for ~150 elites/run). Minimum
      // 45s between elite-chest drops regardless of kill rate — a free
      // legendary is a BEAT, not a conveyor belt.
      // damageEnemy has no run-clock param — wall clock is fine for a
      // spacing throttle (pauses only ever make the gap LONGER).
      const _nowSec = (typeof performance !== 'undefined' ? performance.now() : Date.now()) / 1000;
      const _stateAnyEC = state as unknown as { _lastEliteChestAt?: number };
      if (state.eliteChestCounter >= 10
          && (_nowSec - (_stateAnyEC._lastEliteChestAt ?? -999)) >= 45) {
        state.eliteChestCounter = 0;
        _stateAnyEC._lastEliteChestAt = _nowSec;
        (state.pendingEliteChestSpawns ??= []).push({ x: enemy.x, y: enemy.y });
        __chestAudit.fromEliteGate++; __chestAudit.total++;
      }
    }

    // BETA 20.63 — REMOVED the ungated per-mini-boss chest drop (added
    // 20.58). Measured live (muted bot run): mini-bosses are ~36% of mid-game
    // spawns, so dropping a chest on EVERY one carpeted the floor — hundreds per
    // run vs the ~15 intended (Dylan opened 52 in a 7-min run with hundreds more
    // sitting on the ground). Now there is ONE clean rule: big (elite-variant)
    // Mini-bosses ride the SAME every-10 elite gate above as crowned zombies;
    // normal mini-bosses drop nothing. 1 chest per 10 elites, period.
    // (Per-type audit counters for verification are still incremented above.)

    return { killed: true, drops };
  }

  return { killed: false, drops: [] };
}

/**
 * Remove dead enemies from the array.
 * Call periodically (not every frame) to avoid constant array resizing.
 */
export function cleanupDead(state: EnemySystemState): void {
  state.enemies = state.enemies.filter(e => e.alive);
  // BETA 21.42 — RECONCILE aliveCount to the true alive count. aliveCount is
  // maintained incrementally (++ on spawn, -- at the discrete death sites), but
  // several paths set enemy.alive = false WITHOUT a matching decrement —
  // floor-advance bulk-clear (engine.ts), thorns reflect-kill (~1654), the
  // devour (~3052) — and this filter silently drops them, so the counter drifted
  // UP. Since spawning is GATED on `aliveCount >= getMaxEnemiesAlive`
  // (waveSystem.ts:633), inflation throttled spawns toward zero — the root of
  // "floor 2 announced harder but spawned very few enemies." Setting (not
  // decrementing) here is idempotent and self-heals all drift every cleanup tick.
  state.aliveCount = state.enemies.length;
}

// ═══════════════════════════════════════════════════════════════
//  RENDERING
// ═══════════════════════════════════════════════════════════════

/** Draw image centered at (cx,cy) fitting within `size`, preserving aspect ratio. */
function drawAspect(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  cx: number, cy: number,
  size: number,
): void {
  const natW = img.naturalWidth  || size;
  const natH = img.naturalHeight || size;
  const aspect = natW / natH;
  const dw = aspect >= 1 ? size : size * aspect;
  const dh = aspect >= 1 ? size / aspect : size;
  ctx.drawImage(img, cx - dw / 2, cy - dh / 2, dw, dh);
}

// (E1 enemy rebuild — the old white hit-flash tint helper that lived here was
// deleted: dead since BETA 21.58 "enemies turning white". Bespoke per-enemy
// hit VFX replace it in Phase C, per the VFX identity law.)

// BETA 16.09 — Red silhouette canvas for boss outline. Alpha-mask trick
// (source-atop fill over an offscreen sprite copy) filled red. Used to draw
// a hard sprite-shape outline behind the boss sprite (replaces the
// translucent red disc per Dylan: "i noticed a lot of bosses or
// mini-bosses all have a red circle under their sprite. i dont
// like that. any way to just perfectly outline the sprite with a hard
// red line?")
const _redSilhouetteCache: Map<string, HTMLCanvasElement> = new Map();
function getRedSilhouette(spriteUrl: string, img: HTMLImageElement): HTMLCanvasElement | null {
  if (!img.naturalWidth) return null;
  const cached = _redSilhouetteCache.get(spriteUrl);
  if (cached) return cached;
  const c = document.createElement('canvas');
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const cctx = c.getContext('2d');
  if (!cctx) return null;
  cctx.drawImage(img, 0, 0);
  cctx.globalCompositeOperation = 'source-atop';
  cctx.fillStyle = '#ff1818';   // hard red
  cctx.fillRect(0, 0, c.width, c.height);
  _redSilhouetteCache.set(spriteUrl, c);
  return c;
}

// BETA 20.78 — Frozen-status ice tint. frozenUntil ticked logic-only since
// 13.95 (enemies just stood still with ZERO visual read on WHY). Same
// alpha-mask trick + forever-cache as getRedSilhouette (boss outline):
// build a pale-ice copy once per sprite
// URL, drawImage it over the sprite at partial alpha so only the opaque
// sprite pixels tint blue. The sprite itself is untouched — a status tint
// overlay is the same sanctioned mechanism as the existing hit flash.
const _frozenTintCache: Map<string, HTMLCanvasElement> = new Map();
function getFrozenTint(spriteUrl: string, img: HTMLImageElement): HTMLCanvasElement | null {
  if (!img.naturalWidth) return null;
  const cached = _frozenTintCache.get(spriteUrl);
  if (cached) return cached;
  const c = document.createElement('canvas');
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const cctx = c.getContext('2d');
  if (!cctx) return null;
  cctx.drawImage(img, 0, 0);
  cctx.globalCompositeOperation = 'source-atop';
  cctx.fillStyle = '#8fe3ff';   // pale ice blue
  cctx.fillRect(0, 0, c.width, c.height);
  _frozenTintCache.set(spriteUrl, c);
  return c;
}

function drawAspectCanvas(
  ctx: CanvasRenderingContext2D,
  src: HTMLCanvasElement,
  cx: number, cy: number,
  size: number,
): void {
  const aspect = src.width / src.height;
  const dw = aspect >= 1 ? size : size * aspect;
  const dh = aspect >= 1 ? size / aspect : size;
  ctx.drawImage(src, cx - dw / 2, cy - dh / 2, dw, dh);
}

/**
 * Render all enemies to the canvas.
 * Called from engine.ts render() after drawing the map, before drawing UI.
 */
export function renderEnemies(
  state: EnemySystemState,
  ctx: CanvasRenderingContext2D,
  camera: CameraForEnemy,
): void {
  for (const enemy of state.enemies) {
    if (!enemy.alive) continue;

    // Convert world position to screen position
    const screenX = (enemy.x - camera.x) * camera.zoom + camera.viewportWidth / 2;
    const screenY = (enemy.y - camera.y) * camera.zoom + camera.viewportHeight / 2;

    // ── BETA 20.78 — SLAM TELEGRAPH (fairness warning) ──
    // slamTargetX/Y have existed since the slam shipped but were NEVER
    // rendered: the slammer leapt and the AoE landed with zero warning. While it
    // is AIRBORNE (slamPhase 1) a dark shadow blob now grows at the landing
    // point from 0 to the REAL sa.aoeRadius over the airborne duration —
    // the exact circle the landing damage check uses (visuals only, no
    // gameplay change) — and dirt-crack lines split open in the last 30% of
    // the flight. Drawn BEFORE the off-screen cull because the landing
    // point can be on-screen while the slammer itself is not; it does its own
    // cull. All wobble is deterministic from vertex index + enemy.id (no
    // RNG in render — perf law); the whole block is skipped unless a
    // the slammer is mid-jump (rare, a handful of frames at a time).
    if (enemy.slamPhase === 1) {
      const slamDef = getEnemyDef(enemy.definitionId)?.slamAttack;
      if (slamDef && slamDef.airborneDuration > 0) {
        const slamProg = Math.min(1, Math.max(0, 1 - enemy.slamPhaseTimer / slamDef.airborneDuration));
        const tgx = (enemy.slamTargetX - camera.x) * camera.zoom + camera.viewportWidth / 2;
        const tgy = (enemy.slamTargetY - camera.y) * camera.zoom + camera.viewportHeight / 2;
        const fullR = slamDef.aoeRadius * camera.zoom;
        const growR = fullR * slamProg;
        if (
          growR > 2 &&
          tgx > -fullR && tgx < camera.viewportWidth + fullR &&
          tgy > -fullR && tgy < camera.viewportHeight + fullR
        ) {
          ctx.save();
          // Irregular shadow blob — 10 vertices wobbled 1.02–1.07× the true
          // radius (slightly OVER-warns, never under-warns; and it's a lumpy
          // blob, not a clean circle — identity law).
          ctx.fillStyle = SLAM_SHADOW_FILL;
          ctx.globalAlpha = 0.16 + slamProg * 0.18;
          ctx.beginPath();
          for (let v = 0; v < 10; v++) {
            const va = v * 0.6283185307179586; // 2π/10
            const wob = 1.02 + 0.05 * Math.sin(v * 2.4 + enemy.id * 0.7);
            const vx = tgx + Math.cos(va) * growR * wob;
            const vy = tgy + Math.sin(va) * growR * wob;
            if (v === 0) ctx.moveTo(vx, vy); else ctx.lineTo(vx, vy);
          }
          ctx.closePath();
          ctx.fill();
          // Darker squashed core — the impact center reads heavier, gives
          // the shadow a "mass coming down on THIS spot" focal point.
          ctx.fillStyle = SLAM_CORE_FILL;
          ctx.globalAlpha = 0.22 + slamProg * 0.20;
          ctx.beginPath();
          ctx.ellipse(tgx, tgy, growR * 0.45, growR * 0.34, 0, 0, Math.PI * 2);
          ctx.fill();
          // Last 30% of the flight: 5 jagged crack lines splinter outward
          // (each has a perpendicular mid-point kink so they read as the
          // ground splitting, not wheel spokes).
          if (slamProg > 0.7) {
            ctx.strokeStyle = SLAM_CRACK_STROKE;
            ctx.globalAlpha = ((slamProg - 0.7) / 0.3) * 0.85;
            ctx.lineWidth = Math.max(1.5, 2.5 * camera.zoom);
            ctx.lineCap = 'round';
            ctx.beginPath();
            for (let c = 0; c < 5; c++) {
              const ca = enemy.id * 1.7 + c * 1.2566370614359172 + Math.sin(c * 3.1 + enemy.id) * 0.22; // 2π/5 apart, jittered
              const reach = growR * (0.82 + 0.13 * Math.sin(c * 5.3 + enemy.id * 1.3));
              const midR = reach * 0.55;
              const kink = reach * 0.14 * Math.sin(c * 7.7 + enemy.id * 2.1);
              const cosA = Math.cos(ca), sinA = Math.sin(ca);
              ctx.moveTo(tgx, tgy);
              ctx.lineTo(tgx + cosA * midR - sinA * kink, tgy + sinA * midR + cosA * kink);
              ctx.lineTo(tgx + cosA * reach, tgy + sinA * reach);
            }
            ctx.stroke();
          }
          ctx.restore();
        }
      }
    }

    // Frustum culling — skip if off-screen (margin is 2× sprite so enemies don't pop in at edge)
    const margin = enemy.spriteSize * camera.zoom * 2;
    if (
      screenX < -margin || screenX > camera.viewportWidth + margin ||
      screenY < -margin || screenY > camera.viewportHeight + margin
    ) continue;

    // ── BETA 15.63 — Jump arc visual: sprite bulge + render-side Y-lift ──
    // Per Dylan: "are wea ble to make it so we can see the zombie do the
    // full jump? instead of teleport?" The position is now lerped (15.63
    // movement code), AND we add a render-only Y offset that LIFTS the
    // sprite up at midpoint, simulating an arc. Combined with the 2.2×
    // climbScale, the enemy visibly soars from one side of the wall to
    // the other.
    const isPhasing = enemy.phaseTimer > 0;
    let climbScale = 1;
    let arcLiftY = 0;
    if (isPhasing && enemy.phaseMaxTimer > 0) {
      const progress = 1 - enemy.phaseTimer / enemy.phaseMaxTimer; // 0→1
      climbScale = 1 + Math.sin(progress * Math.PI) * 1.2;         // peak 2.2×
      // Y-lift: pulls sprite UP at midpoint to simulate jump apex.
      // Magnitude: half the sprite height — clearly readable arc.
      arcLiftY = -Math.sin(progress * Math.PI) * enemy.spriteSize * 0.5 * camera.zoom;
    }
    const renderSize = enemy.spriteSize * camera.zoom * climbScale;
    // Adjust screenY for arc-lift (only affects the sprite + visual layers,
    // not collision / hp-bar anchor — those use the unmodified screenY).
    const spriteScreenY = screenY + arcLiftY;

    // (E1 enemy rebuild — the DISABLED charging-ring (21.56 "the red ring
    // warning sucks") and elite/boss impact-ring (21.58 anti-ring) blocks that
    // lived here were deleted. Phase C ships bespoke NON-RING telegraphs and
    // hit VFX per enemy — the VFX identity law. wallPressTimer + flashTimer
    // state both stay live for those replacements.)

    // ── Draw sprite ──
    const spriteUrl = enemy.currentSprite;
    const img = spriteCache.get(spriteUrl);

    if (img && img.complete && img.naturalWidth > 0) {
      const def2 = getEnemyDef(enemy.definitionId);
      const shouldFlip = def2?.flipHorizontal && enemy.facing === 'right';

      // BETA 16.09 — Per Dylan: "i noticed a lot of bosses or timmy's and
      // bosses all have a red circle under their sprite. i dont like
      // that. any way to just perfectly outline the sprite with a hard
      // red line?" Replaced the translucent red disc with a HARD RED
      // sprite-shape outline. Technique: draw the red silhouette canvas
      // (alpha-masked sprite filled red) at 4 cardinal offsets behind
      // the actual sprite. Where the offsets stick out past the sprite
      // shape, the red shows = exact silhouette outline. The actual
      // sprite then draws on top untouched.
      if (enemy.variant === 'boss') {
        const redSilhouette = getRedSilhouette(spriteUrl, img);
        if (redSilhouette) {
          // 2-pixel outline thickness (scales with zoom for visibility)
          const offset = Math.max(2, Math.round(2 * camera.zoom));
          // 8 offsets for a smooth rounded outline (cardinals + diagonals)
          const offsets: [number, number][] = [
            [-offset, 0], [offset, 0], [0, -offset], [0, offset],
            [-offset, -offset], [offset, -offset], [-offset, offset], [offset, offset],
          ];
          for (const [dx, dy] of offsets) {
            drawAspectCanvas(ctx, redSilhouette, screenX + dx, spriteScreenY + dy, renderSize);
          }
        }
      }

      // Sprite draw — uses spriteScreenY (= screenY + arcLiftY) so the sprite
      // visibly lifts up at jump apex. BETA 15.63.
      // (E1 enemy rebuild — the dead 21.58 white hit-flash branch that lived
      // here was deleted; both branches had become identical. Hit read =
      // damage numbers + knockback. flashTimer still ticks for the Phase C
      // bespoke per-enemy hit VFX.)
      if (shouldFlip) {
        ctx.translate(screenX, spriteScreenY);
        ctx.scale(-1, 1);
        drawAspect(ctx, img, 0, 0, renderSize);
        ctx.setTransform(1, 0, 0, 1, 0, 0);
      } else {
        drawAspect(ctx, img, screenX, spriteScreenY, renderSize);
      }

      // ── BETA 20.78 — FROZEN READ (Stay Frosty / Timmy Repellent etc.) ──
      // frozenUntil was logic-only: a frozen enemy just stood there with no
      // visual cue. Now: ice-blue sprite tint (cached alpha mask — sprite
      // untouched underneath) + two STATIC
      // ice spikes at the sprite base. Static on purpose — frozen means
      // nothing moves. HOT PATH: the entire block is gated on the frozen
      // check; zero extra work when not frozen. No RNG, no allocations.
      if (enemy.frozenUntil > 0) {
        const frozenTint = getFrozenTint(spriteUrl, img);
        if (frozenTint) {
          ctx.globalAlpha = 0.55;
          if (shouldFlip) {
            ctx.translate(screenX, spriteScreenY);
            ctx.scale(-1, 1);
            drawAspectCanvas(ctx, frozenTint, 0, 0, renderSize);
            ctx.setTransform(1, 0, 0, 1, 0, 0);
          } else {
            drawAspectCanvas(ctx, frozenTint, screenX, spriteScreenY, renderSize);
          }
          ctx.globalAlpha = 1.0;
        }
        // Two asymmetric spikes (tall left, stubby right) in two different
        // blues so the pair reads as jagged ice, not mirrored clip-art.
        const iceS = renderSize * 0.13;
        const iceY = screenY + renderSize * 0.42;
        ctx.fillStyle = FROZEN_SPIKE_LIGHT;
        ctx.beginPath();
        ctx.moveTo(screenX - iceS * 1.7, iceY);
        ctx.lineTo(screenX - iceS * 0.95, iceY - iceS * 1.9);
        ctx.lineTo(screenX - iceS * 0.2, iceY);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = FROZEN_SPIKE_DEEP;
        ctx.beginPath();
        ctx.moveTo(screenX + iceS * 0.35, iceY + iceS * 0.15);
        ctx.lineTo(screenX + iceS * 1.05, iceY - iceS * 1.15);
        ctx.lineTo(screenX + iceS * 1.75, iceY + iceS * 0.15);
        ctx.closePath();
        ctx.fill();
      }
    } else {
      // Fallback: draw a colored circle if sprite not loaded
      ctx.fillStyle = enemy.variant === 'elite' ? '#FFD700' : '#7CFC00';
      ctx.beginPath();
      ctx.arc(screenX, spriteScreenY, renderSize / 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#00BFFF';
      ctx.fillRect(screenX + renderSize / 6, spriteScreenY - renderSize / 4, 6, 10);
    }

    // Restore alpha after sprite draw
    if (isPhasing) ctx.globalAlpha = 1;

    // ── BETA 15.62 — "JUMP!" text — STATIC POSITION, NO SCALING ──
    // Per Dylan: "the jump word JUMP should not increase or decrease in
    // size. it should stay the same siz." The font size IS constant, but
    // the Y position was anchored to `renderSize` which includes the climb-
    // scale (1 → 2.2 → 1 sprite bulge). So as the sprite ballooned, the
    // text floated UP; as the sprite shrank, the text floated DOWN — even
    // though the font itself never changed size, the apparent "size of the
    // jump area" pulsed. Fix: anchor Y to BASE sprite size (no climb
    // scaling), no float-up animation. Text stays rock-solid above enemy.
    if (isPhasing && enemy.phaseMaxTimer > 0) {
      const phaseProgress = 1 - enemy.phaseTimer / enemy.phaseMaxTimer; // 0→1
      const fade = phaseProgress < 0.85 ? 1 : (1 - (phaseProgress - 0.85) / 0.15);
      // Use BASE sprite size (no climbScale) so text doesn't bob with bulge.
      const baseSize = enemy.spriteSize * camera.zoom;
      const textY = screenY - baseSize / 2 - 18 * camera.zoom;
      const fontSize = Math.max(24, 32 * camera.zoom);
      ctx.save();
      ctx.globalAlpha = fade;
      ctx.font = `bold ${Math.round(fontSize)}px "Share Tech Mono", monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 6;
      ctx.strokeText('JUMP!', screenX, textY);
      ctx.fillStyle = '#ffe833';
      ctx.fillText('JUMP!', screenX, textY);
      ctx.restore();
    }

    // ── HP bar — always visible above head ──
    if (true) { // was: only show if damaged — now always on
      const isBoss = enemy.variant === 'boss';
      const barWidth = renderSize * (isBoss ? 1.0 : 0.8);
      const barHeight = (isBoss ? 8 : 4) * camera.zoom;
      const barX = screenX - barWidth / 2;
      const barY = screenY - renderSize / 2 - barHeight - (isBoss ? 8 : 4);
      const hpPercent = Math.max(0, enemy.currentHP / enemy.maxHP);

      // Background
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(barX, barY, barWidth, barHeight);

      // HP fill — always red (darker when nearly dead)
      const hpColor = hpPercent > 0.25 ? '#EF4444' : '#991111';
      ctx.fillStyle = hpColor;
      ctx.fillRect(barX, barY, barWidth * hpPercent, barHeight);

      // Border
      ctx.strokeStyle = isBoss ? 'rgba(255,60,60,0.8)' : 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = isBoss ? 2 : 1;
      ctx.strokeRect(barX, barY, barWidth, barHeight);

      // Shield bar above HP bar (boss mechanic)
      if (enemy.maxShieldHP > 0) {
        const shBarH = (isBoss ? 6 : 4) * camera.zoom;
        const shBarY = barY - shBarH - 3 * camera.zoom;
        const shPct = Math.max(0, enemy.shieldHP / enemy.maxShieldHP);

        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.fillRect(barX, shBarY, barWidth, shBarH);
        ctx.fillStyle = '#44aaff';
        ctx.fillRect(barX, shBarY, barWidth * shPct, shBarH);
        ctx.strokeStyle = 'rgba(100,180,255,0.6)';
        ctx.lineWidth = 1;
        ctx.strokeRect(barX, shBarY, barWidth, shBarH);
      }
    }

    // ── Elite crown indicator (no emoji — canvas emoji is slow) ──
    if (enemy.variant === 'elite') {
      const crownY = screenY - renderSize / 2 - 14;
      const crownR  = Math.round(6 * camera.zoom);
      ctx.fillStyle = '#FFD700';
      ctx.beginPath();
      ctx.moveTo(screenX,          crownY - crownR);
      ctx.lineTo(screenX + crownR, crownY);
      ctx.lineTo(screenX,          crownY + crownR * 0.6);
      ctx.lineTo(screenX - crownR, crownY);
      ctx.closePath();
      ctx.fill();
    }
  }

  // ── Render enemy projectiles (penguin weapons, etc.) ──
  for (const proj of _projectiles) {
    const screenX = (proj.x - camera.x) * camera.zoom + camera.viewportWidth / 2;
    const screenY = (proj.y - camera.y) * camera.zoom + camera.viewportHeight / 2;

    const margin = 80 * camera.zoom;
    if (
      screenX < -margin || screenX > camera.viewportWidth + margin ||
      screenY < -margin || screenY > camera.viewportHeight + margin
    ) continue;

    const renderSize = 52 * camera.zoom * proj.scale;
    const img = spriteCache.get(proj.spriteUrl);

    ctx.save();
    ctx.translate(screenX, screenY);
    ctx.rotate(proj.rotation);

    // BETA 16.11 — Per Dylan: "i noticed the boss sprite with red outline
    // does not have all their sprites outlined. most bosses have multiple
    // sprite for one enemy. make snese?" Boss-thrown projectiles (Kevin
    // throw, etc.) ALSO had the legacy red disc that 16.09 fixed for the
    // boss body. Replaced with the same hard sprite-shape outline so
    // every boss-related sprite reads consistently.
    //
    // BETA 16.55 — PSBOT cycle-30l: Dylan died to a boss throw he
    // couldn't see ("his throwable needs to be outlined in red. the
    // outline of the zombie throw sprite. so i can see it"). The 2px
    // offset was barely visible at typical zoom + 4× projectile speed
    // ramp. Bumped to 6px + added a pulsing red shadow glow so incoming
    // throws read as DANGER from across the map.
    // BETA 17.11 — Dylan ask cycle-30ab: render red outline for ANY thrown
    // projectile that should be highlighted (boss variant OR heavy source).
    // Previously gate was just `proj.isBoss` which missed non-boss heavies.
    if ((proj.isBoss || proj.shouldOutline) && img && img.complete && img.naturalWidth > 0) {
      const redSilhouette = getRedSilhouette(proj.spriteUrl, img);
      if (redSilhouette) {
        // Pulsing offset 5–7px (zoom-aware) — eye-catching at any zoom level
        const pulseT = (Date.now() / 200) % (Math.PI * 2);
        const pulseScale = 1 + Math.sin(pulseT) * 0.2; // ±20% pulse
        const baseOffset = Math.max(6, Math.round(6 * camera.zoom));
        const offset = Math.max(4, Math.round(baseOffset * pulseScale));
        const offsets: [number, number][] = [
          [-offset, 0], [offset, 0], [0, -offset], [0, offset],
          [-offset, -offset], [offset, -offset], [-offset, offset], [offset, offset],
        ];
        for (const [dx, dy] of offsets) {
          drawAspectCanvas(ctx, redSilhouette, dx, dy, renderSize);
        }

        // Bright red shadow-glow layer behind the sprite — broadcasts
        // DANGER even when silhouette pixels are hard to read against
        // similarly-colored ground. Pulses with the silhouette offset.
        // BETA 16.59 — Wave 4: gated by graphicsSettings.glowEffectsEnabled.
        // Players on low-end devices can disable shadowBlur to save
        // major per-frame perf (shadowBlur is one of the heaviest 2D
        // canvas ops). Outline silhouette pass above keeps working —
        // only the soft glow drops out.
        // BETA 16.95 — Visual LOD (PSBOT cycle-30i §7B): when entity
        // count is high (>300 alive enemies), skip the per-enemy glow
        // shadow pass. Outline silhouette pass above still runs so
        // enemies remain readable against ground; only the soft red
        // halo drops out. shadowBlur is one of the heaviest 2D canvas
        // ops — at 300+ enemies the cost compounds quickly.
        const _glowSkipForLOD = state.aliveCount > 300;
        if (_glowEffectsEnabled() && !_glowSkipForLOD) {
          const glowAlpha = 0.55 + Math.sin(pulseT) * 0.25; // 0.30–0.80
          const prevAlpha = ctx.globalAlpha;
          const prevShadowColor = ctx.shadowColor;
          const prevShadowBlur = ctx.shadowBlur;
          ctx.globalAlpha = glowAlpha;
          ctx.shadowColor = '#ff0000';
          ctx.shadowBlur = 24 * camera.zoom;
          drawAspectCanvas(ctx, redSilhouette, 0, 0, renderSize);
          ctx.globalAlpha = prevAlpha;
          ctx.shadowColor = prevShadowColor;
          ctx.shadowBlur = prevShadowBlur;
        }
      }
    }

    if (img && img.complete && img.naturalWidth > 0) {
      drawAspect(ctx, img, 0, 0, renderSize);
    } else {
      // Fallback circle while sprite loads
      ctx.fillStyle = proj.isBoss ? '#ff6622' : '#cccccc';
      ctx.beginPath();
      ctx.arc(0, 0, proj.radius * camera.zoom, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}

// ═══════════════════════════════════════════════════════════════
//  EAT ATTACK SYSTEM
// ═══════════════════════════════════════════════════════════════

/**
 * Tick the eat-a-zombie attack (def-driven: any enemy with eatAttack).
 * Returns true while he is mid-eat (grab / hold / chomp phases) so the caller
 * can show the correct sprite and suppress normal movement + throw/slam.
 * Any loot from the devoured zombie is pushed into dropsOut.
 */
function tickEatAttack(
  eater: EnemyInstance,
  ea: EatAttackDef,
  dt: number,
  enemies: EnemyInstance[],
  dropsOut: EnemyLootDrop[],
): boolean {
  const cooldown = eater.variant === 'boss' ? ea.bossCooldown : ea.cooldown;

  // ── Phase 1: GRAB — move toward the victim ───────────────────
  if (eater.eatPhase === 1) {
    eater.eatPhaseTimer -= dt;
    // Shuffle toward the target zombie while grabbing
    const target = enemies.find(e => e.id === eater.eatTargetId && e.alive);
    if (target) {
      const dx = target.x - eater.x;
      const dy = target.y - eater.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 8) {
        const step = eater.speed * 1.4 * dt;
        eater.x += (dx / dist) * Math.min(step, dist);
        eater.y += (dy / dist) * Math.min(step, dist);
      }
    }
    if (eater.eatPhaseTimer <= 0) {
      // Transition to HOLD — mark zombie as devoured (alive = false, no kill credit)
      const victim = enemies.find(e => e.id === eater.eatTargetId && e.alive);
      if (victim) {
        victim.alive = false;
        // Zombie drops its loot at its position (player can pick up free XP)
        const victimDef = getEnemyDef(victim.definitionId);
        if (victimDef) dropsOut.push(...rollEnemyDrops(victimDef, victim.variant, 1.0));
      }
      eater.eatPhase = 2;
      eater.eatPhaseTimer = ea.holdDuration;
    }
    return true;
  }

  // ── Phase 2: HOLD BEFORE EAT ────────────────────────────────
  if (eater.eatPhase === 2) {
    eater.eatPhaseTimer -= dt;
    if (eater.eatPhaseTimer <= 0) {
      eater.eatPhase = 3;
      eater.eatPhaseTimer = ea.eatDuration;
      // BETA 21.17 — Dylan: every eat = heal + grow + more damage + faster, stacking.
      // BETA 21.22 — the 21.17 FULL heal made the eater UNKILLABLE in god mode (infinite
      // zombies → he eats + full-heals whenever you get him low, forever — LogiCC's
      // stuck run). Now heals a big chunk (healPct, 35%) not to full, so he's still a
      // scary self-healing growing tank but you CAN out-damage him over a few cycles.
      eater.currentHP     = Math.min(eater.maxHP, eater.currentHP + eater.maxHP * ea.healPct);
      eater.spriteSize    *= (1 + ea.growPct);
      eater.hitboxRadius  *= (1 + ea.growPct);
      // BETA 15.46 — pathingRadius stays capped so post-eat eater still
      // fits through doorways even after multiple eat-stack growth.
      eater.pathingRadius = Math.min(eater.hitboxRadius, PATHING_RADIUS_CAP);
      // damage persists (set once at spawn) and speed persists (the slow system
      // multiplies-then-restores relative to enemy.speed), so a plain *= sticks.
      eater.damage *= (1 + (ea.dmgPct ?? 0.12));               // +12% damage / eat
      eater.speed  *= (1 + (ea.speedPct ?? 0.06));             // +6% move speed / eat
    }
    return true;
  }

  // ── Phase 3: EATING — chomp animation ───────────────────────
  if (eater.eatPhase === 3) {
    eater.eatPhaseTimer -= dt;
    if (eater.eatPhaseTimer <= 0) {
      eater.eatPhase = 0;
      eater.eatTargetId = -1;
      eater.eatTimer = cooldown;
    }
    return true;
  }

  // ── Phase 0: IDLE — count down, then look for a snack ───────
  eater.eatTimer -= dt;
  if (eater.eatTimer > 0) return false;

  // Only eat if there's a nearby edible enemy AND the eater wants to eat.
  // He's more willing when low on HP.
  const hpFraction = eater.currentHP / eater.maxHP;
  const urgency = hpFraction < ea.preferBelowHpPct ? 1.0 : 0.35;
  if (Math.random() > urgency) {
    eater.eatTimer = 3.0; // not hungry right now, check again soon
    return false;
  }

  // Find the nearest edible enemy
  let bestDist = ea.eatRange * ea.eatRange;
  let bestTarget: EnemyInstance | null = null;
  for (const e of enemies) {
    if (!e.alive || e.id === eater.id) continue;
    if (!ea.edibleIds.includes(e.definitionId)) continue;
    const dx = e.x - eater.x;
    const dy = e.y - eater.y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestDist) { bestDist = d2; bestTarget = e; }
  }

  if (!bestTarget) {
    eater.eatTimer = 4.0; // nothing nearby, try again soon
    return false;
  }

  // Lock target and start grab phase
  eater.eatTargetId = bestTarget.id;
  eater.eatPhase = 1;
  eater.eatPhaseTimer = ea.grabDuration;
  return true;
}

// ═══════════════════════════════════════════════════════════════
//  SLAM ATTACK SYSTEM
// ═══════════════════════════════════════════════════════════════

/**
 * Tick the slam attack each frame (def-driven: any enemy with slamAttack).
 * Returns true while the enemy is mid-slam (airborne or landing) so the caller
 * can suppress normal movement and set the correct sprite.
 */
function tickSlamAttack(
  enemy: EnemyInstance,
  sa: SlamAttackDef,
  dt: number,
  player: PlayerForEnemy,
): boolean {
  const cooldown = enemy.variant === 'boss' ? sa.bossCooldown : sa.cooldown;

  // ── Phase 1: AIRBORNE — fly toward slam target ──────────────
  if (enemy.slamPhase === 1) {
    enemy.slamPhaseTimer -= dt;

    // Move rapidly toward the captured target position
    const dx = enemy.slamTargetX - enemy.x;
    const dy = enemy.slamTargetY - enemy.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > 4) {
      const step = sa.moveSpeed * dt;
      enemy.x += (dx / dist) * Math.min(step, dist);
      enemy.y += (dy / dist) * Math.min(step, dist);
    }

    if (enemy.slamPhaseTimer <= 0) {
      // LAND — snap to target, deal AoE damage if player is within radius
      enemy.x = enemy.slamTargetX;
      enemy.y = enemy.slamTargetY;
      enemy.slamPhase = 2;
      enemy.slamPhaseTimer = sa.landingDuration;
      playBossSlam();
      // BETA 16.33 — Dylan: "game should shake on every [boss] jump
      // attack when he hits the ground pretty wild like." Big chunky
      // shake at the moment of impact regardless of whether the player
      // got hit by the AoE. Boss variants get a stronger shake.
      // BETA 16.52 — a mega-tier final boss gets the EXTREME shake.
      // BETA 16.53 — pass impact point + AoE so the engine can crush
      // rooms within the landing radius (mega-only behavior).
      if (_slamImpactShakeCb) _slamImpactShakeCb(
        enemy.variant === 'boss',
        (getEnemyDef(enemy.definitionId)?.tags ?? []).includes('mega'),   // 23.39: mega-tier by tag, no hardcoded id
        enemy.x, enemy.y, sa.aoeRadius,
      );

      const pdx = player.x - enemy.x;
      const pdy = player.y - enemy.y;
      if (pdx * pdx + pdy * pdy <= sa.aoeRadius * sa.aoeRadius) {
        // BETA 14.73 — BUG-4 from cycle-8: removed `&& player.iFrameTimer <= 0`
        // outer gate. Previously, slam landings during a player iframe silently
        // skipped player.damage() entirely, producing a math gap (attempted ≠
        // hit + missed buckets). Now the damage callback fires every time and
        // routes iframe-blocked attempts to missedByIframe internally.
        // BETA 14.59 — slam attack source attribution for deathContext.
        setLastDamageSource(`${enemy.definitionId}_slam`);
        const slamDmg = enemy.damage * sa.damageMult;
        const _wasIframeBlocked = player.iFrameTimer > 0;
        player.damage(slamDmg, 0.6, `${enemy.definitionId}_slam`);
        // BETA 21.55 — Dylan: "[the boss] jumps on me, I should be FLUNG across the map."
        // The slam went through a SEPARATE damage path with ZERO knockback (the
        // contact-hit fling at ~1614 never ran for slams). Now a heavy directional
        // launch away from the slam center — SET (not accumulated) so it overrides
        // the swarm-cancel and clearly hurls you out. Wall-checked in the engine
        // apply so it can't shove you through walls.
        {
          const _pp = player as unknown as { x: number; y: number; knockbackX?: number; knockbackY?: number };
          const _sdx = player.x - enemy.x, _sdy = player.y - enemy.y;
          const _sd = Math.hypot(_sdx, _sdy) || 1;
          const _slamForce = Math.min(3000, 1100 + slamDmg * 6);
          _pp.knockbackX = (_sdx / _sd) * _slamForce;
          _pp.knockbackY = (_sdy / _sd) * _slamForce;
        }
        // BETA 14.71 — BUG-9 from cycle-8: boss_slam_crack floorEffectTracker
        // had spawns + enemyTriggers populating but dmgDealt = 0. Slam damage
        // was going via player.damage direct path that didn't credit the floor
        // effect. Now it does via the floorEffect tick tracker.
        // BETA 14.73 — only credit floor-effect damage when not iframe-blocked
        // so analyst sees true damage-dealt, not theoretical.
        if (!_wasIframeBlocked) {
          trackFloorEffectTick(`${enemy.definitionId}_slam_crack`, slamDmg);
        }
      }

      // BETA 13.101 — Spawn the crack overlay as a persistent ground effect
      // at the impact point. Lives for 60s, renders UNDER enemies/player via
      // the ground-effects render pass (engine.ts:1977 — runs before
      // renderEnemies). Sprite-only (no slow / no DoT — purely cosmetic).
      const ws = getWeaponSystemState();
      if (ws && sa.crackSprite) {
        spawnGroundEffectAt(ws, enemy.slamTargetX, enemy.slamTargetY, {
          radius: sa.aoeRadius,
          lifetime: 60.0,
          slowMult: 1.0,         // no slow effect
          dotPerSec: 0,          // no DoT
          color: 'rgba(0,0,0,0)', // invisible disc; sprite is the visual
          weaponId: 'boss_slam_crack',   // 23.39: renamed from caseoh_slam_crack — PSBOT handoff sent
          groundSpriteUrl: sa.crackSprite,
        });
      }
    }
    return true;
  }

  // ── Phase 2: LANDING — crack overlay lingers ─────────────────
  if (enemy.slamPhase === 2) {
    enemy.slamPhaseTimer -= dt;
    if (enemy.slamPhaseTimer <= 0) {
      enemy.slamPhase = 0;
      enemy.slamTimer = cooldown;
    }
    return true;
  }

  // ── Phase 0: IDLE — count down to next slam ──────────────────
  enemy.slamTimer -= dt;
  if (enemy.slamTimer > 0) return false;

  const dx = player.x - enemy.x;
  const dy = player.y - enemy.y;
  if (dx * dx + dy * dy <= sa.maxRange * sa.maxRange) {
    enemy.slamPhase = 1;
    enemy.slamPhaseTimer = sa.airborneDuration;
    enemy.slamTargetX = player.x;
    enemy.slamTargetY = player.y;
  } else {
    enemy.slamTimer = 1.5; // retry soon
  }
  return false;
}

// ═══════════════════════════════════════════════════════════════
//  THROW ATTACK SYSTEM
// ═══════════════════════════════════════════════════════════════

function tickThrowAttack(
  enemy: EnemyInstance,
  ta: ThrowAttackDef,
  dt: number,
  player: PlayerForEnemy,
): void {
  const cooldown = enemy.variant === 'boss' ? ta.bossCooldown : ta.cooldown;

  // ── Windup phase: count down, then release ──
  if (enemy.throwWindupTimer > 0) {
    enemy.throwWindupTimer -= dt;
    if (enemy.throwWindupTimer <= 0) {
      enemy.throwWindupTimer = 0;
      spawnEnemyProjectile(enemy, ta, player);
      // BETA 21.59 — Dylan: "the closer they are, the faster + MORE FREQUENT their
      // throws, on a scale curve, so it FORCES me to run from them." Proximity
      // multiplier on the cooldown: 1.0× at maxRange → 0.25× right on top of you,
      // via an ease-in curve (closeness²) so the danger spikes sharply up close.
      // Floored at 0.3s so it can never go infinite. Standing next to a thrower
      // now means a face-full of projectiles — you have to keep moving.
      const _pdx = player.x - enemy.x, _pdy = player.y - enemy.y;
      const _pdist = Math.sqrt(_pdx * _pdx + _pdy * _pdy);
      const _span = Math.max(1, ta.maxRange - ta.minRange);
      const _close = Math.min(1, Math.max(0, (ta.maxRange - _pdist) / _span)); // 1 = closest
      const _mult = 1 - 0.75 * _close * _close;                                // 1.0 far → 0.25 close
      enemy.throwTimer = Math.max(0.3, cooldown * _mult);
    }
    return;
  }

  // ── Cooldown phase: count down, then check range ──
  enemy.throwTimer -= dt;
  if (enemy.throwTimer > 0) return;

  const dx = player.x - enemy.x;
  const dy = player.y - enemy.y;
  const dist = Math.sqrt(dx * dx + dy * dy);

  if (dist >= ta.minRange && dist <= ta.maxRange) {
    // In range — start windup, lock target.
    // BETA 21.59 — proximity also shortens the wind-up (faster reaction up close),
    // same ease-in curve as the cooldown above. 1.0× wind-up at maxRange → 0.55× on
    // top of you. Keeps a readable telegraph at range but snaps fast point-blank.
    const _wSpan = Math.max(1, ta.maxRange - ta.minRange);
    const _wClose = Math.min(1, Math.max(0, (ta.maxRange - dist) / _wSpan));
    enemy.throwWindupTimer = ta.windup * (1 - 0.45 * _wClose * _wClose);
    enemy.throwTargetX = player.x;
    enemy.throwTargetY = player.y;
  } else {
    // Out of range — retry soon
    enemy.throwTimer = 1.0;
  }
}

// BETA 14.51 — Hard cap on enemy projectile pool. OOM crash hunt: this array
// was unbounded. At 20-min difficulty + max-weapons, enemies throw faster than
// projectiles expire (player dodges → no on-hit despawn; projectiles in flight
// keep ticking until maxDist). Each projectile is a non-trivial object holding
// position/velocity/sprite ref — at thousands of entries the heap balloons and
// the per-frame tickProjectiles loop chokes. User reported "renderer crashed
// after about 1 minute. level 20 minutes setting using max weapons."
const MAX_ENEMY_PROJECTILES = 200;

function spawnEnemyProjectile(enemy: EnemyInstance, ta: ThrowAttackDef, player: PlayerForEnemy): void {
  // Aim at current player position (where they ARE now, not where they were at windup start)
  const dx = player.x - enemy.x;
  const dy = player.y - enemy.y;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  const isBoss = enemy.variant === 'boss';
  const scale = isBoss ? ta.bossScale : 1.0;

  // BETA 14.51 — drop-oldest if at cap. Game-feel impact is negligible because
  // 200 simultaneous enemy projectiles already saturates the screen visually.
  if (_projectiles.length >= MAX_ENEMY_PROJECTILES) {
    _projectiles.shift();
  }

  // BETA 16.31 — apply time-remaining speed ramp (set by enemyAdapter each tick)
  const projSpeed = ta.projectileSpeed * _enemyProjSpeedMult;
  _projectiles.push({
    id: _nextProjectileId++,
    x: enemy.x,
    y: enemy.y,
    vx: (dx / dist) * projSpeed,
    vy: (dy / dist) * projSpeed,
    damage: Math.max(1, Math.round(enemy.damage * ta.projectileDamageMultiplier)),
    radius: ta.projectileRadius * scale,
    spriteUrl: ta.projectileSprite,
    rotation: Math.atan2(dy, dx),
    rotationSpeed: 8.0,
    ownerId: enemy.id,
    ownerType: enemy.definitionId,
    isBoss,
    // BETA 17.11 — outline any heavy-thrown projectile (regardless of variant)
    // OR any boss throw. Dylan-direct ask cycle-30ab.
    shouldOutline: true,  // BETA 21.49 — Dylan: EVERY enemy throwable gets the red outline (was only boss + heavies; timmy/other throws were hard to see). Makes incoming throws readable.
    scale,
    travelDist: 0,
    maxDist: ta.projectileRange * scale,
  });
  loadSprite(ta.projectileSprite);
}

// BETA 23.6x — CO-OP PHASE 2 part D: takes the full players array so enemy
// projectiles can hit ANY living view, not just the primary (pre-part-D a
// throw flew straight through p2..p4). Views are tested in roster order; the
// FIRST view the projectile overlaps takes the damage and consumes the
// projectile — exactly one hit per projectile, same despawn as before. Dead
// views (currentHP <= 0) don't absorb projectiles. Single-player fast-path:
// one view, no filter — byte-identical to the old singular-player path.
function tickProjectiles(players: PlayerForEnemy[], dt: number, gameState?: GameState): void {
  const views = players.length === 1 ? players : players.filter(pv => pv.currentHP > 0);
  for (let i = _projectiles.length - 1; i >= 0; i--) {
    const p = _projectiles[i];

    const moveX = p.vx * dt;
    const moveY = p.vy * dt;
    const prevX = p.x;
    const prevY = p.y;
    p.x += moveX;
    p.y += moveY;
    p.travelDist += Math.sqrt(moveX * moveX + moveY * moveY);
    p.rotation += p.rotationSpeed * dt;

    // BETA 21.48 — Dylan: "throwables should have a forever range — only
    // disappear if they hit you or a wall." Removed the maxDist range despawn;
    // the enclosed-map wall raycast (below) + the player collision are now the
    // ONLY ways a throw ends. The map is wall-bounded so every throw eventually
    // hits a wall, and MAX_ENEMY_PROJECTILES (drop-oldest) is the runaway net.
    // (travelDist still ticks above — harmless, kept in case other code reads it.)

    // ── BETA 15.40 — Wall collision (anti-tunneling raycast) ──
    // Step from prev position to new position in small increments. If any
    // step lands on a non-walkable tile, kill the projectile at that point.
    // Without this, fast projectiles tunnel through 96wu thick walls.
    if (gameState?.map?.roomMap) {
      const moveLen = Math.sqrt(moveX * moveX + moveY * moveY);
      const STEP = 16; // wu — finer than wall thickness (96wu) to prevent tunneling
      const steps = Math.max(1, Math.ceil(moveLen / STEP));
      let killed = false;
      for (let s = 1; s <= steps; s++) {
        const t = s / steps;
        const sx = prevX + moveX * t;
        const sy = prevY + moveY * t;
        if (!isWalkable(gameState, sx, sy)) {
          if (_projImpactCb) _projImpactCb(sx, sy, p.isBoss); // BETA 20.78 — splat at the wall
          _projectiles.splice(i, 1);
          killed = true;
          break;
        }
      }
      if (killed) continue;
    }

    // Player collision
    // BETA 14.73 — BUG-4 from cycle-8: removed outer iframe gate. Projectile
    // collisions during an iframe used to silently pass through with no
    // attempt counted; now player.damage() always fires and routes iframe to
    // missedByIframe internally. Projectile is consumed either way (it hit
    // the player visually).
    // BETA 23.6x — CO-OP PHASE 2 part D: every living view is a valid target;
    // roster order, first overlap wins, projectile consumed. SP: views = [p1]
    // — one iteration, identical math and identical damage call.
    for (const pv of views) {
      const dx = pv.x - p.x;
      const dy = pv.y - p.y;
      if (dx * dx + dy * dy < (pv.hitboxRadius + p.radius) ** 2) {
        // BETA 14.59 — projectile source attribution for deathContext.
        // BETA 20.86 — name the actual THROWER (Dylan: "will it say if i die
        // from a throwable from a mini-boss"). p.ownerType is the
        // thrower's definitionId, so a fatal throw now reads '<enemy>_throw' /
        // 'timmy_throw' instead of the generic 'enemy_projectile' that the
        // death-attribution code couldn't resolve to a name. Legacy fallback
        // keeps the boss/regular distinction if ownerType is somehow missing.
        // (setLastDamageSource stays as the SP-compat mirror; the source also
        // rides the per-view damage call itself — part B §5.)
        setLastDamageSource(
          p.ownerType ? `${p.ownerType}_throw` : (p.isBoss ? 'boss_projectile' : 'enemy_projectile'),
        );
        pv.damage(p.damage, 0.35,
          p.ownerType ? `${p.ownerType}_throw` : (p.isBoss ? 'boss_projectile' : 'enemy_projectile'));
        if (_projImpactCb) _projImpactCb(p.x, p.y, p.isBoss); // BETA 20.78 — splat on the player
        _projectiles.splice(i, 1);
        break;
      }
    }
  }
}

// ═══════════════════════════════════════════════════════════════
//  QUERY HELPERS
// ═══════════════════════════════════════════════════════════════

/** Get all alive enemies */
export function getAliveEnemies(state: EnemySystemState): EnemyInstance[] {
  return state.enemies.filter(e => e.alive);
}

/** Get count of alive enemies — O(1), tracked incrementally */
export function getAliveCount(state: EnemySystemState): number {
  return state.aliveCount;
}

/** Find the nearest alive enemy to a point */
export function getNearestEnemy(
  state: EnemySystemState,
  x: number,
  y: number,
): EnemyInstance | null {
  let nearest: EnemyInstance | null = null;
  let nearestDist = Infinity;

  for (const enemy of state.enemies) {
    if (!enemy.alive) continue;
    const dist = distance(enemy.x, enemy.y, x, y);
    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = enemy;
    }
  }

  return nearest;
}

/** Get all alive enemies within a radius of a point */
export function getEnemiesInRadius(
  state: EnemySystemState,
  x: number,
  y: number,
  radius: number,
): EnemyInstance[] {
  return state.enemies.filter(e => {
    if (!e.alive) return false;
    return distance(e.x, e.y, x, y) <= radius;
  });
}

/**
 * Check if a circle (projectile) collides with any enemy.
 * Returns the first enemy hit, or null.
 */
export function checkProjectileCollision(
  state: EnemySystemState,
  projX: number,
  projY: number,
  projRadius: number,
): EnemyInstance | null {
  for (const enemy of state.enemies) {
    if (!enemy.alive) continue;
    const dist = distance(enemy.x, enemy.y, projX, projY);
    if (dist < enemy.hitboxRadius + projRadius) {
      return enemy;
    }
  }
  return null;
}

// ─── Utility ──────────────────────────────────────────────────

function distance(x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

// Track last horizontal direction per enemy for pure-down movement
const lastHorizontalFacing: Map<number, 'left' | 'right'> = new Map();

function getFacingDirection(
  fromX: number, fromY: number,
  toX: number, toY: number,
  enemyId: number = 0,
): SpriteDirection {
  const dx = toX - fromX;
  const dy = toY - fromY;

  // 3-sprite system: up, left, right. No 'down' sprite.
  //
  // Moving UP (any upward component dominant) → 'up'
  // Moving LEFT (any leftward component) → 'left'
  // Moving RIGHT (any rightward component) → 'right'
  // Moving DIRECTLY DOWN (no horizontal component) → use last horizontal facing

  // If moving upward and vertical component is dominant
  if (dy < 0 && Math.abs(dy) > Math.abs(dx)) {
    return 'up';
  }

  // If there's any horizontal component, use it
  if (Math.abs(dx) > 0.01) {
    const horizontal: 'left' | 'right' = dx > 0 ? 'right' : 'left';
    lastHorizontalFacing.set(enemyId, horizontal);
    return horizontal;
  }

  // Moving directly down with no horizontal component:
  // Use the last known horizontal direction, default to 'left'
  return lastHorizontalFacing.get(enemyId) ?? 'left';
}
